import {_private as themeDefinitions} from '../../themes/generated/generated-definitions';

const {Themes} = themeDefinitions;
type Themes = typeof Themes;

interface GradientColor {
  color:
    | 'BLACK_500'
    | 'WHITE_500'
    | 'gradient.start'
    | 'gradient.mid'
    | 'gradient.end'
    | 'gradient.primary'
    | 'gradient.secondary';
  saturation?: number | string;
  lightness?: number | string;
  opacity?: number;
}

export type SemanticColors = Record<
  string,
  Record<
    Themes[keyof Themes],
    {
      raw: keyof RawColors;
      opacity?: number;
    }
  > & {
    category: 'background' | 'border' | 'text' | 'generic';
    gradient?: {dark: GradientColor; light: GradientColor};
  }
>;
const SemanticColors = {
  ACTION_SHEET_GRADIENT_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {
      light: {color: 'gradient.mid', lightness: 0.925, saturation: '*0.8'},
      dark: {color: 'gradient.mid', lightness: 0.12, saturation: '*0.5'},
    },
  },
  ACTIVITY_CARD_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 1},
  },
  ACTIVITY_CARD_ICON_OVERLAY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 0.85},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 0.85},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_600', opacity: 0.85},
    [Themes.DARKER]: {raw: 'PRIMARY_600', opacity: 0.85},
  },
  ALERT_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
  },
  ANDROID_NAVIGATION_BAR_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_830', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_24', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_24', opacity: 1},
  },
  ANDROID_NAVIGATION_SCRIM_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_830', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 0.5},
    [Themes.MIDNIGHT]: {raw: 'PLUM_24', opacity: 0.5},
    [Themes.DARKER]: {raw: 'PLUM_24', opacity: 0.5},
  },
  ANDROID_RIPPLE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.07},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.12},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0.07},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0.07},
  },
  AUTOCOMPLETE_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_630', opacity: 1},
  },
  BACKGROUND_ACCENT: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_15', opacity: 1},
    gradient: {
      light: {color: 'gradient.mid', opacity: 0.4, lightness: '*0.75'},
      dark: {color: 'gradient.mid', opacity: 0.3, lightness: 0.75, saturation: '*1.2'},
    },
  },
  BACKGROUND_FLOATING: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
  },
  BACKGROUND_MENTIONED: {
    category: 'background',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'YELLOW_300', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_260', opacity: 0.08},
    [Themes.DARKER]: {raw: 'YELLOW_260', opacity: 0.08},
  },
  BACKGROUND_MENTIONED_HOVER: {
    category: 'background',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'YELLOW_300', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 0.08},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 0.08},
  },
  BACKGROUND_MESSAGE_AUTOMOD: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_400', opacity: 0.05},
    [Themes.LIGHT]: {raw: 'RED_400', opacity: 0.05},
    [Themes.MIDNIGHT]: {raw: 'RED_345', opacity: 0.08},
    [Themes.DARKER]: {raw: 'RED_345', opacity: 0.08},
  },
  BACKGROUND_MESSAGE_AUTOMOD_HOVER: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_400', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'RED_400', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'RED_400', opacity: 0.1},
    [Themes.DARKER]: {raw: 'RED_400', opacity: 0.1},
  },
  BACKGROUND_MESSAGE_HIGHLIGHT: {
    category: 'background',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'BRAND_360', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_360', opacity: 0.08},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 0.08},
  },
  BACKGROUND_MESSAGE_HIGHLIGHT_HOVER: {
    category: 'background',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 0.06},
    [Themes.LIGHT]: {raw: 'BRAND_360', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'BRAND_360', opacity: 0.06},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 0.06},
  },
  BACKGROUND_MESSAGE_HOVER: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_900', opacity: 0.06},
    [Themes.LIGHT]: {raw: 'PRIMARY_900', opacity: 0.03},
    [Themes.MIDNIGHT]: {raw: 'PLUM_21', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_21', opacity: 1},
  },
  BACKGROUND_MOBILE_PRIMARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
  },
  BACKGROUND_MOBILE_SECONDARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.6}, dark: {color: 'BLACK_500', opacity: 0.4}},
  },
  BACKGROUND_MODIFIER_ACCENT: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.48},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.24},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23_ALPHA', opacity: 0.1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
  },
  BACKGROUND_MODIFIER_ACTIVE: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.48},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 0.48},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 0.48},
  },
  BACKGROUND_MODIFIER_HOVER: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 0.3},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 0.3},
  },
  BACKGROUND_MODIFIER_SELECTED: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.6},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.24},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 0.6},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 0.6},
  },
  BACKGROUND_NESTED_FLOATING: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_22', opacity: 1},
    gradient: {light: {color: 'BLACK_500', opacity: 0.1}, dark: {color: 'WHITE_500', opacity: 0.1}},
  },
  BACKGROUND_PRIMARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.5}, dark: {color: 'BLACK_500', opacity: 0.5}},
  },
  BACKGROUND_SECONDARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
  },
  BACKGROUND_SECONDARY_ALT: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_15', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.5}, dark: {color: 'BLACK_500', opacity: 0.5}},
  },
  BACKGROUND_TERTIARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_16', opacity: 1},
  },
  BADGE_BRAND_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'BRAND_260', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_260', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_260', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_260', opacity: 1},
  },
  BADGE_BRAND_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_560', opacity: 1},
  },
  BG_BACKDROP: {
    category: 'background',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.7},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.54},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 0.75},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.7},
    gradient: {
      light: {color: 'gradient.start', saturation: 0.2, lightness: 0.3, opacity: 0.7},
      dark: {color: 'gradient.start', saturation: 0.25, lightness: 0.1, opacity: 0.7},
    },
  },
  BG_BACKDROP_IMMERSIVE: {
    category: 'background',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.85},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.8},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 0.9},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.85},
  },
  BG_BACKDROP_NO_OPACITY: {
    category: 'background',
    [Themes.DARK]: {raw: 'BLACK', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 1},
  },
  BG_BASE_PRIMARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.8}, dark: {color: 'BLACK_500', opacity: 0.7}},
  },
  BG_BASE_SECONDARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.8}, dark: {color: 'BLACK_500', opacity: 0.7}},
  },
  BG_BASE_TERTIARY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_23', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.8}, dark: {color: 'BLACK_500', opacity: 0.7}},
  },
  BG_BRAND: {
    category: 'background',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  BG_MOD_FAINT: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.16},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
    gradient: {light: {color: 'BLACK_500', opacity: 0.05}, dark: {color: 'WHITE_500', opacity: 0.05}},
  },
  BG_MOD_STRONG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.54},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.24},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.18},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.24},
    gradient: {light: {color: 'BLACK_500', opacity: 0.1}, dark: {color: 'WHITE_500', opacity: 0.1}},
  },
  BG_MOD_SUBTLE: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.48},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.12},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.16},
    gradient: {light: {color: 'BLACK_500', opacity: 0.075}, dark: {color: 'WHITE_500', opacity: 0.075}},
  },
  BG_MODAL_GRADIENT_GREEN_END: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_GREEN_DARK_2', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_GREEN_LIGHT_2', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_GREEN_DARK_2', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_GREEN_DARK_2', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_GREEN_START: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_GREEN_DARK_1', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_GREEN_LIGHT_1', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_GREEN_DARK_1', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_GREEN_DARK_1', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_LAVENDER_END: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_DARK_2', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_LIGHT_2', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_DARK_2', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_DARK_2', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_LAVENDER_START: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_DARK_1', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_LIGHT_1', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_DARK_1', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_LAVENDER_DARK_1', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_MAGENTA_END: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_2', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_LIGHT_2', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_2', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_2', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_MAGENTA_START: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_1', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_LIGHT_1', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_1', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_1', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_NAVY_END: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_NAVY_DARK_2', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_NAVY_LIGHT_2', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_NAVY_DARK_2', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_NAVY_DARK_2', opacity: 0.3},
  },
  BG_MODAL_GRADIENT_NAVY_START: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_NAVY_DARK_1', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_NAVY_LIGHT_1', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_NAVY_DARK_1', opacity: 0.25},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_NAVY_DARK_1', opacity: 0.3},
  },
  BG_SURFACE_OVERLAY: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.5}, dark: {color: 'BLACK_500', opacity: 0.5}},
  },
  BG_SURFACE_OVERLAY_TMP: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_21', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.5}, dark: {color: 'BLACK_500', opacity: 0.5}},
  },
  BG_SURFACE_RAISED: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.5}, dark: {color: 'BLACK_500', opacity: 0.5}},
  },
  BG_TILE_GRADIENT_MAGENTA_END: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_2', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_LIGHT_2', opacity: 0.3},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_2', opacity: 0.3},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_2', opacity: 0.3},
  },
  BG_TILE_GRADIENT_MAGENTA_START: {
    category: 'background',
    [Themes.DARK]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_1', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_LIGHT_1', opacity: 0.3},
    [Themes.MIDNIGHT]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_1', opacity: 0.3},
    [Themes.DARKER]: {raw: 'BG_MODAL_GRADIENT_MAGENTA_DARK_1', opacity: 0.3},
  },
  BLACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 1},
  },
  BLUR_FALLBACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.96},
    [Themes.LIGHT]: {raw: 'PRIMARY_530', opacity: 0.96},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 0.96},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 0.96},
  },
  BLUR_FALLBACK_PRESSED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_730', opacity: 0.96},
    [Themes.LIGHT]: {raw: 'PRIMARY_560', opacity: 0.96},
    [Themes.MIDNIGHT]: {raw: 'PLUM_20', opacity: 0.96},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 0.96},
  },
  BORDER_FAINT: {
    category: 'border',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.03},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.04},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.08},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.06},
  },
  BORDER_STRONG: {
    category: 'border',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.16},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.24},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.22},
  },
  BORDER_SUBTLE: {
    category: 'border',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.14},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
  },
  BUG_REPORTER_MODAL_SUBMITTING_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 0.6},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.6},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 0.6},
    [Themes.DARKER]: {raw: 'PLUM_23', opacity: 0.6},
  },
  BUTTON_CREATOR_REVENUE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'TEAL_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_430', opacity: 1},
    [Themes.DARKER]: {raw: 'TEAL_430', opacity: 1},
  },
  BUTTON_DANGER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  BUTTON_DANGER_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_530', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_530', opacity: 1},
  },
  BUTTON_DANGER_BACKGROUND_DISABLED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  BUTTON_DANGER_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_500', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_500', opacity: 1},
  },
  BUTTON_DANGER_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 0},
  },
  BUTTON_FILLED_BRAND_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  BUTTON_FILLED_BRAND_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_600', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_600', opacity: 1},
  },
  BUTTON_FILLED_BRAND_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_560', opacity: 1},
  },
  BUTTON_FILLED_BRAND_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 0},
  },
  BUTTON_FILLED_BRAND_INVERTED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_FILLED_BRAND_INVERTED_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_200', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_200', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_200', opacity: 1},
  },
  BUTTON_FILLED_BRAND_INVERTED_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_100', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_100', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_100', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_100', opacity: 1},
  },
  BUTTON_FILLED_BRAND_INVERTED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  BUTTON_FILLED_BRAND_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_FILLED_WHITE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 1},
  },
  BUTTON_FILLED_WHITE_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 1},
  },
  BUTTON_FILLED_WHITE_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 1},
  },
  BUTTON_FILLED_WHITE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_500', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0},
  },
  BUTTON_OUTLINE_BRAND_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_560', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_BORDER_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_560', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_BORDER_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_TEXT_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_BRAND_TEXT_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0},
  },
  BUTTON_OUTLINE_DANGER_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_460', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_460', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_460', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_400', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_400', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_BORDER_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_BORDER_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_TEXT_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_DANGER_TEXT_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0},
  },
  BUTTON_OUTLINE_POSITIVE_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_530', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_BORDER_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_530', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_BORDER_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_TEXT_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_POSITIVE_TEXT_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0},
  },
  BUTTON_OUTLINE_PRIMARY_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_500', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_BORDER_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_BORDER_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_TEXT_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_OUTLINE_PRIMARY_TEXT_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  BUTTON_POSITIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  BUTTON_POSITIVE_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_530', opacity: 1},
  },
  BUTTON_POSITIVE_BACKGROUND_DISABLED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  BUTTON_POSITIVE_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_530', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_500', opacity: 1},
  },
  BUTTON_POSITIVE_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 0},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 0},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 0},
  },
  BUTTON_SECONDARY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
  },
  BUTTON_SECONDARY_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_10', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
  },
  BUTTON_SECONDARY_BACKGROUND_DISABLED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
  },
  BUTTON_SECONDARY_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  BUTTON_SECONDARY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 1},
  },
  BUTTON_TRANSPARENT_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.01},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 0.1},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 0.1},
  },
  BUTTON_TRANSPARENT_BACKGROUND_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 0.01},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.25},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 0.01},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 0.01},
  },
  BUTTON_TRANSPARENT_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE_500', opacity: 0.05},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'WHITE_500', opacity: 0.05},
    [Themes.DARKER]: {raw: 'WHITE_500', opacity: 0.05},
  },
  BUTTON_TRANSPARENT_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_2', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_3', opacity: 1},
  },
  CARD_GRADIENT_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.4},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.7},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.4},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.4},
  },
  CARD_GRADIENT_PRESSED_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.5},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.5},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.5},
  },
  CARD_PRIMARY_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.75}, dark: {color: 'WHITE_500', opacity: 0.1}},
  },
  CARD_PRIMARY_PRESSED_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_645', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_25', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_19', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.075}, dark: {color: 'WHITE_500', opacity: 0.05}},
  },
  CARD_SECONDARY_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_25', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_19', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.4}, dark: {color: 'WHITE_500', opacity: 0.1}},
  },
  CARD_SECONDARY_PRESSED_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_645', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_26', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_21', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.75}, dark: {color: 'WHITE_500', opacity: 0.05}},
  },
  CHANNEL_ICON: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_10', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
  },
  CHANNEL_TEXT_AREA_PLACEHOLDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  CHANNELS_DEFAULT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_9', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_9', opacity: 1},
  },
  CHANNELTEXTAREA_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_15', opacity: 1},
  },
  CHAT_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_16', opacity: 1},
  },
  CHAT_BANNER_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'gradient.start'}, dark: {color: 'gradient.start', opacity: 1}},
  },
  CHAT_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_20', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
  },
  CHAT_INPUT_CONTAINER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_16', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_16', opacity: 1},
  },
  CHAT_SWIPE_TO_REPLY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_22', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_22', opacity: 1},
  },
  CHAT_SWIPE_TO_REPLY_GRADIENT_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.4},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.1},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.1},
  },
  COACHMARK_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
  },
  CONTENT_INVENTORY_MEDIA_SEEKBAR_CONTAINER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PLUM_6', opacity: 0.24},
    [Themes.LIGHT]: {raw: 'PLUM_6', opacity: 0.24},
    [Themes.MIDNIGHT]: {raw: 'PLUM_6', opacity: 0.24},
    [Themes.DARKER]: {raw: 'PLUM_6', opacity: 0.24},
  },
  CONTENT_INVENTORY_OVERLAY_TEXT_PRIMARY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.85},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.85},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0.85},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0.85},
  },
  CONTENT_INVENTORY_OVERLAY_TEXT_SECONDARY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.7},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.7},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0.7},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0.7},
  },
  CONTENT_INVENTORY_OVERLAY_UI_MOD: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.7},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.7},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0.7},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0.7},
  },
  CONTENT_INVENTORY_OVERLAY_UI_MOD_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.1},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.1},
  },
  CONTEXT_MENU_BACKDROP_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.7},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.54},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.7},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.7},
  },
  CONTROL_BRAND_FOREGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_360', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  CONTROL_BRAND_FOREGROUND_NEW: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_360', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  CREATOR_REVENUE_ICON_GRADIENT_END: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'TEAL_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_430', opacity: 1},
    [Themes.DARKER]: {raw: 'TEAL_430', opacity: 1},
  },
  CREATOR_REVENUE_ICON_GRADIENT_START: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'TEAL_345', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_360', opacity: 1},
    [Themes.DARKER]: {raw: 'TEAL_360', opacity: 1},
  },
  CREATOR_REVENUE_INFO_BOX_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_430', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'TEAL_430', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_430', opacity: 0.1},
    [Themes.DARKER]: {raw: 'TEAL_430', opacity: 0.1},
  },
  CREATOR_REVENUE_INFO_BOX_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'TEAL_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_400', opacity: 1},
    [Themes.DARKER]: {raw: 'TEAL_400', opacity: 1},
  },
  CREATOR_REVENUE_LOCKED_CHANNEL_ICON: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_345', opacity: 1},
    [Themes.LIGHT]: {raw: 'TEAL_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_345', opacity: 1},
    [Themes.DARKER]: {raw: 'TEAL_345', opacity: 1},
  },
  CREATOR_REVENUE_PROGRESS_BAR: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TEAL_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'TEAL_345', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'TEAL_400', opacity: 1},
    [Themes.DARKER]: {raw: 'TEAL_400', opacity: 1},
  },
  CUSTOM_STATUS_BUBBLE_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
  },
  DEPRECATED_CARD_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.6},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 0.6},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 0.6},
  },
  DEPRECATED_CARD_EDITABLE_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 0.6},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 0.3},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 0.3},
  },
  DEPRECATED_TEXT_INPUT_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 1},
  },
  DEPRECATED_TEXT_INPUT_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 0.3},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.3},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.3},
  },
  DEPRECATED_TEXT_INPUT_BORDER_DISABLED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 1},
  },
  DEPRECATED_TEXT_INPUT_BORDER_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_900', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_300', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_900', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_900', opacity: 1},
  },
  DISPLAY_BANNER_OVERFLOW_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.5},
    [Themes.MIDNIGHT]: {raw: 'PLUM_20', opacity: 0.5},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 0.5},
  },
  DIVIDER_STRONG: {
    category: 'border',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.16},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.24},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.22},
  },
  DIVIDER_SUBTLE: {
    category: 'border',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.12},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
  },
  EMBED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.25}, dark: {color: 'WHITE_500', opacity: 0.1}},
  },
  EMBED_BACKGROUND_ALTERNATE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_600', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.25}, dark: {color: 'WHITE_500', opacity: 0.075}},
  },
  EMBED_TITLE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_2', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_3', opacity: 1},
  },
  EXPRESSION_PICKER_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'gradient.end', lightness: '*1.05'}, dark: {color: 'gradient.end', lightness: '*0.95'}},
  },
  FOCUS_PRIMARY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLUE_345', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLUE_360', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLUE_345', opacity: 1},
    [Themes.DARKER]: {raw: 'BLUE_345', opacity: 1},
  },
  FORUM_POST_EXTRA_MEDIA_COUNT_CONTAINER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 0.8},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 0.8},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 0.8},
    [Themes.DARKER]: {raw: 'PLUM_19', opacity: 0.8},
  },
  FORUM_POST_TAG_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 0.9},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 0.9},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 0.9},
    [Themes.DARKER]: {raw: 'PLUM_19', opacity: 0.9},
  },
  GUILD_NOTIFICATIONS_BOTTOM_SHEET_PILL_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 1},
  },
  HEADER_MUTED: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_10', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
  },
  HEADER_PRIMARY: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_2', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_3', opacity: 1},
  },
  HEADER_SECONDARY: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_8', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_9', opacity: 1},
  },
  HOME_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_645', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_100', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_18', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
  },
  ICON_MUTED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_12', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  ICON_PRIMARY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_5', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_4', opacity: 1},
  },
  ICON_SECONDARY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_9', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_9', opacity: 1},
  },
  ICON_TRANSPARENT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TRANSPARENT', opacity: 0},
    [Themes.LIGHT]: {raw: 'TRANSPARENT', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'TRANSPARENT', opacity: 0},
    [Themes.DARKER]: {raw: 'TRANSPARENT', opacity: 0},
  },
  INFO_BOX_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLUE_345', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'BLUE_345', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'BLUE_345', opacity: 0.1},
    [Themes.DARKER]: {raw: 'BLUE_345', opacity: 0.1},
  },
  INFO_DANGER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_400', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'RED_400', opacity: 0.1},
    [Themes.DARKER]: {raw: 'RED_400', opacity: 0.1},
  },
  INFO_DANGER_FOREGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_400', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_400', opacity: 1},
  },
  INFO_DANGER_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  INFO_HELP_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLUE_345', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'BLUE_430', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'BLUE_345', opacity: 0.1},
    [Themes.DARKER]: {raw: 'BLUE_345', opacity: 0.1},
  },
  INFO_HELP_FOREGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLUE_345', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLUE_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLUE_345', opacity: 1},
    [Themes.DARKER]: {raw: 'BLUE_345', opacity: 1},
  },
  INFO_HELP_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  INFO_POSITIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'GREEN_400', opacity: 0.05},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 0.1},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 0.1},
  },
  INFO_POSITIVE_FOREGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 1},
  },
  INFO_POSITIVE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  INFO_WARNING_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'YELLOW_400', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 0.1},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 0.1},
  },
  INFO_WARNING_FOREGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.LIGHT]: {raw: 'YELLOW_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 1},
  },
  INFO_WARNING_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  INPUT_BACKGROUND: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23_ALPHA', opacity: 0.1},
    [Themes.DARKER]: {raw: 'PLUM_24', opacity: 0.85},
    gradient: {light: {color: 'BLACK_500', opacity: 0.1}, dark: {color: 'BLACK_500', opacity: 0.3}},
  },
  INPUT_BORDER: {
    category: 'background',
    [Themes.DARK]: {raw: 'PLUM_11', opacity: 0.32},
    [Themes.LIGHT]: {raw: 'PLUM_11', opacity: 0.32},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.32},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.32},
  },
  INPUT_ERROR_BACKGROUND: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_500', opacity: 0.12},
    [Themes.LIGHT]: {raw: 'RED_500', opacity: 0.12},
    [Themes.MIDNIGHT]: {raw: 'RED_500', opacity: 0.12},
    [Themes.DARKER]: {raw: 'RED_500', opacity: 0.12},
  },
  INPUT_ERROR_BORDER: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_360', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_360', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_360', opacity: 1},
  },
  INPUT_ERROR_TEXT: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_260', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_700', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_260', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_260', opacity: 1},
  },
  INPUT_FOCUSED_BORDER: {
    category: 'background',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.16},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.24},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.32},
  },
  INPUT_PLACEHOLDER_TEXT: {
    category: 'background',
    [Themes.DARK]: {raw: 'INPUT_PLACEHOLDER_TEXT_DARK', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_9', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
    gradient: {light: {color: 'BLACK_500', opacity: 0.6}, dark: {color: 'WHITE_500', opacity: 0.55}},
  },
  INTERACTIVE_ACTIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_3', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_1', opacity: 1},
  },
  INTERACTIVE_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_4', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_4', opacity: 1},
  },
  INTERACTIVE_MUTED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_300', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
    gradient: {light: {color: 'BLACK_500', opacity: 0.3}, dark: {color: 'WHITE_500', opacity: 0.3}},
  },
  INTERACTIVE_NORMAL: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_6', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_6', opacity: 1},
  },
  LEADERBOARD_LEAGUE_BRAND_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'LOL_TEXT_LIGHT', opacity: 1},
    [Themes.LIGHT]: {raw: 'LOL_TEXT_DARK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'LOL_TEXT_LIGHT', opacity: 1},
    [Themes.DARKER]: {raw: 'LOL_TEXT_LIGHT', opacity: 1},
  },
  LEGACY_ANDROID_BLUR_OVERLAY_DEFAULT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_660', opacity: 0.5},
    [Themes.DARKER]: {raw: 'PRIMARY_660', opacity: 0.5},
  },
  LEGACY_ANDROID_BLUR_OVERLAY_ULTRA_THIN: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.025},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.25},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.025},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.025},
  },
  LEGACY_BLUR_FALLBACK_DEFAULT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 0.975},
    [Themes.LIGHT]: {raw: 'PRIMARY_230', opacity: 0.975},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_660', opacity: 0.975},
    [Themes.DARKER]: {raw: 'PRIMARY_660', opacity: 0.975},
  },
  LEGACY_BLUR_FALLBACK_ULTRA_THIN: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.95},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.95},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.95},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.95},
  },
  LIVE_STAGE_TILE_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.6},
    [Themes.LIGHT]: {raw: 'PRIMARY_430', opacity: 0.15},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 0.6},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 0.6},
  },
  LOGO_PRIMARY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  MENTION_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0.15},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 0.5},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 0.5},
  },
  MENTION_FOREGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_260', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_230', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_230', opacity: 1},
  },
  MENU_ITEM_DANGER_ACTIVE_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_530', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_530', opacity: 1},
  },
  MENU_ITEM_DANGER_HOVER_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  MENU_ITEM_DEFAULT_ACTIVE_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'BRAND_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_560', opacity: 1},
  },
  MENU_ITEM_DEFAULT_HOVER_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  MESSAGE_REACTED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 0.15},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0.15},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 0.15},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 0.15},
  },
  MESSAGE_REACTED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_200', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_200', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_200', opacity: 1},
  },
  MODAL_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_16', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_16', opacity: 1},
  },
  MODAL_FOOTER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
  },
  NAVIGATOR_HEADER_TINT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  OVERLAY_BACKDROP_DEFAULT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'OPACITY_BLACK_72', opacity: 0.7215686274509804},
    [Themes.LIGHT]: {raw: 'OPACITY_BLACK_52', opacity: 0.5215686274509804},
    [Themes.MIDNIGHT]: {raw: 'OPACITY_BLACK_72', opacity: 0.7215686274509804},
    [Themes.DARKER]: {raw: 'OPACITY_BLACK_72', opacity: 0.7215686274509804},
  },
  OVERLAY_BACKDROP_LIGHTBOX: {
    category: 'generic',
    [Themes.DARK]: {raw: 'OPACITY_BLACK_92', opacity: 0.9215686274509803},
    [Themes.LIGHT]: {raw: 'OPACITY_BLACK_92', opacity: 0.9215686274509803},
    [Themes.MIDNIGHT]: {raw: 'OPACITY_BLACK_92', opacity: 0.9215686274509803},
    [Themes.DARKER]: {raw: 'OPACITY_BLACK_92', opacity: 0.9215686274509803},
  },
  PANEL_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_24', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.2}, dark: {color: 'BLACK_500', opacity: 0.2}},
  },
  POLLS_NORMAL_FILL_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_600', opacity: 1},
  },
  POLLS_NORMAL_IMAGE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_660', opacity: 1},
  },
  POLLS_VICTOR_FILL: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 0.2},
    [Themes.LIGHT]: {raw: 'GREEN_400', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 0.2},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 0.2},
  },
  POLLS_VOTED_FILL: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 0.2},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 0.2},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 0.2},
  },
  PREMIUM_NITRO_PINK_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PREMIUM_NITRO_PINK_LIGHT', opacity: 1},
    [Themes.LIGHT]: {raw: 'PREMIUM_NITRO_PINK_DARK', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PREMIUM_NITRO_PINK_LIGHT', opacity: 1},
    [Themes.DARKER]: {raw: 'PREMIUM_NITRO_PINK_LIGHT', opacity: 1},
  },
  PROFILE_GRADIENT_NOTE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.3},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.3},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.3},
  },
  PROFILE_GRADIENT_OVERLAY: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.6},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.6},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.6},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.6},
  },
  PROFILE_GRADIENT_OVERLAY_SYNCED_WITH_USER_THEME: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.8},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.8},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.8},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.8},
  },
  PROFILE_GRADIENT_ROLE_PILL_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.5},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_660', opacity: 0.5},
    [Themes.DARKER]: {raw: 'PRIMARY_660', opacity: 0.5},
  },
  PROFILE_GRADIENT_ROLE_PILL_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.2},
    [Themes.LIGHT]: {raw: 'PRIMARY_660', opacity: 0.2},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 0.2},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 0.2},
  },
  PROFILE_GRADIENT_SECTION_BOX: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.45},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.45},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.45},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.45},
  },
  REDESIGN_ACTIVITY_CARD_BADGE_ICON: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
  },
  REDESIGN_BUTTON_ACTIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  REDESIGN_BUTTON_ACTIVE_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_460', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_460', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_460', opacity: 1},
  },
  REDESIGN_BUTTON_ACTIVE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_DANGER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  REDESIGN_BUTTON_DANGER_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_460', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_460', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_460', opacity: 1},
  },
  REDESIGN_BUTTON_DANGER_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_DESTRUCTIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  REDESIGN_BUTTON_DESTRUCTIVE_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_460', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_460', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_460', opacity: 1},
  },
  REDESIGN_BUTTON_DESTRUCTIVE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_OVERLAY_ALPHA_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.54},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.54},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.54},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.54},
  },
  REDESIGN_BUTTON_OVERLAY_ALPHA_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.64},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.64},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.64},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.64},
  },
  REDESIGN_BUTTON_OVERLAY_ALPHA_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_OVERLAY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_OVERLAY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_860', opacity: 1},
  },
  REDESIGN_BUTTON_POSITIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  REDESIGN_BUTTON_POSITIVE_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_460', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_460', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_460', opacity: 1},
  },
  REDESIGN_BUTTON_POSITIVE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_PREMIUM_PRIMARY_BLUE_FOR_GRADIENT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PREMIUM_TIER_0_BLUE_FOR_GRADIENTS_2', opacity: 1},
    [Themes.LIGHT]: {raw: 'PREMIUM_TIER_0_BLUE_FOR_GRADIENTS_2', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PREMIUM_TIER_0_BLUE_FOR_GRADIENTS_2', opacity: 1},
    [Themes.DARKER]: {raw: 'PREMIUM_TIER_0_BLUE_FOR_GRADIENTS_2', opacity: 1},
  },
  REDESIGN_BUTTON_PREMIUM_PRIMARY_PINK_FOR_GRADIENT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PREMIUM_TIER_2_PINK_FOR_GRADIENTS', opacity: 1},
    [Themes.LIGHT]: {raw: 'PREMIUM_TIER_2_PINK_FOR_GRADIENTS', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PREMIUM_TIER_2_PINK_FOR_GRADIENTS', opacity: 1},
    [Themes.DARKER]: {raw: 'PREMIUM_TIER_2_PINK_FOR_GRADIENTS', opacity: 1},
  },
  REDESIGN_BUTTON_PREMIUM_PRIMARY_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.1},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.1},
  },
  REDESIGN_BUTTON_PREMIUM_PRIMARY_PURPLE_FOR_GRADIENT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS', opacity: 1},
    [Themes.LIGHT]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS', opacity: 1},
    [Themes.DARKER]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS', opacity: 1},
  },
  REDESIGN_BUTTON_PREMIUM_PRIMARY_PURPLE_FOR_GRADIENT_2: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS_2', opacity: 1},
    [Themes.LIGHT]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS_2', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS_2', opacity: 1},
    [Themes.DARKER]: {raw: 'PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS_2', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 0},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 0},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 0},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_400', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_ON_BLURPLE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_530', opacity: 0},
    [Themes.LIGHT]: {raw: 'BRAND_530', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'BRAND_530', opacity: 0},
    [Themes.DARKER]: {raw: 'BRAND_530', opacity: 0},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_ON_BLURPLE_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_ON_BLURPLE_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_530', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_530', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_ON_BLURPLE_PRESSED_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_360', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_360', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_ON_BLURPLE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_700', opacity: 0.16},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0.06},
    [Themes.MIDNIGHT]: {raw: 'BRAND_700', opacity: 0.16},
    [Themes.DARKER]: {raw: 'BRAND_700', opacity: 0.16},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_PRESSED_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_400', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'BRAND_330', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_400', opacity: 0.5},
    [Themes.DARKER]: {raw: 'BRAND_400', opacity: 0.5},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_PRESSED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_360', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ALT_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_400', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_ON_BLURPLE_PRESSED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_530', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_530', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_530', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_OVERLAY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_OVERLAY_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_4', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_4', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_OVERLAY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_25', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_25', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_560', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_560', opacity: 1},
  },
  REDESIGN_BUTTON_PRIMARY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_SECONDARY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_15', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.72}, dark: {color: 'WHITE_500', opacity: 0.24}},
  },
  REDESIGN_BUTTON_SECONDARY_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.14},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
    gradient: {light: {color: 'WHITE_500', opacity: 0}, dark: {color: 'WHITE_500', opacity: 0}},
  },
  REDESIGN_BUTTON_SECONDARY_OVERLAY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.54},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.54},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.54},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.54},
  },
  REDESIGN_BUTTON_SECONDARY_OVERLAY_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.64},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.64},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.64},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.64},
  },
  REDESIGN_BUTTON_SECONDARY_OVERLAY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  REDESIGN_BUTTON_SECONDARY_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_15', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
    gradient: {light: {color: 'WHITE_500', opacity: 0.62}, dark: {color: 'WHITE_500', opacity: 0.34}},
  },
  REDESIGN_BUTTON_SECONDARY_PRESSED_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'TRANSPARENT', opacity: 0},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.04},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.08},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.06},
    gradient: {light: {color: 'WHITE_500', opacity: 0}, dark: {color: 'WHITE_500', opacity: 0}},
  },
  REDESIGN_BUTTON_SECONDARY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_4', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_6', opacity: 1},
  },
  REDESIGN_BUTTON_SELECTED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 0.24},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 0.24},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 0.24},
  },
  REDESIGN_BUTTON_SELECTED_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 0.28},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 0.24},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 0.28},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 0.28},
  },
  REDESIGN_BUTTON_SELECTED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  REDESIGN_BUTTON_TERTIARY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23_ALPHA', opacity: 0.1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
    gradient: {light: {color: 'gradient.secondary', opacity: 0.4}, dark: {color: 'WHITE_500', opacity: 0.1}},
  },
  REDESIGN_BUTTON_TERTIARY_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_560', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.2},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.2},
    gradient: {light: {color: 'gradient.secondary', opacity: 0.3}, dark: {color: 'WHITE_500', opacity: 0.2}},
  },
  REDESIGN_BUTTON_TERTIARY_PRESSED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_6', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_5', opacity: 1},
  },
  REDESIGN_BUTTON_TERTIARY_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_4', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_6', opacity: 1},
  },
  REDESIGN_CHANNEL_NAME_MUTED_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_10', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
  },
  REDESIGN_CHANNEL_NAME_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_860', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_2', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_3', opacity: 1},
  },
  REDESIGN_CHAT_INPUT_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23_ALPHA', opacity: 0.1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
  },
  REDESIGN_IMAGE_BUTTON_PRESSED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0.08},
  },
  REDESIGN_INPUT_CONTROL_ACTIVE_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_645', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_16', opacity: 1},
  },
  REDESIGN_INPUT_CONTROL_SELECTED: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BRAND_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_500', opacity: 1},
  },
  SCROLLBAR_AUTO_SCROLLBAR_COLOR_THUMB: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_730', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_20', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_21', opacity: 1},
  },
  SCROLLBAR_AUTO_SCROLLBAR_COLOR_TRACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
  },
  SCROLLBAR_AUTO_THUMB: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_730', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_300', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_16', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_21', opacity: 1},
  },
  SCROLLBAR_AUTO_TRACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_25', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_19', opacity: 1},
  },
  SCROLLBAR_THIN_THUMB: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_730', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 0.3},
    [Themes.MIDNIGHT]: {raw: 'PLUM_20', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_21', opacity: 1},
  },
  SCROLLBAR_THIN_TRACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLACK', opacity: 0},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 0},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 0},
  },
  SPINE_DEFAULT: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_300', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
  },
  SPOILER_HIDDEN_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_14', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_14', opacity: 1},
  },
  SPOILER_REVEALED_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_660', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_160', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.16},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.16},
  },
  STAGE_CARD_PILL_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_25', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_19', opacity: 1},
    gradient: {light: {color: 'gradient.mid', lightness: 0.98}, dark: {color: 'gradient.mid', lightness: 0.16}},
  },
  STATUS_DANGER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_400', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_400', opacity: 1},
  },
  STATUS_DANGER_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_430', opacity: 1},
  },
  STATUS_DANGER_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  STATUS_DND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'RED_400', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_400', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_400', opacity: 1},
  },
  STATUS_IDLE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.LIGHT]: {raw: 'YELLOW_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 1},
  },
  STATUS_OFFLINE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_9', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_9', opacity: 1},
  },
  STATUS_ONLINE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 1},
  },
  STATUS_POSITIVE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 1},
  },
  STATUS_POSITIVE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_430', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_430', opacity: 1},
  },
  STATUS_POSITIVE_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
  STATUS_SPEAKING: {
    category: 'generic',
    [Themes.DARK]: {raw: 'GREEN_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_360', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_360', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_360', opacity: 1},
  },
  STATUS_WARNING: {
    category: 'generic',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.LIGHT]: {raw: 'YELLOW_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 1},
  },
  STATUS_WARNING_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.LIGHT]: {raw: 'YELLOW_400', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 1},
  },
  STATUS_WARNING_TEXT: {
    category: 'text',
    [Themes.DARK]: {raw: 'BLACK', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'BLACK', opacity: 1},
  },
  TEXT_BRAND: {
    category: 'text',
    [Themes.DARK]: {raw: 'BRAND_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'BRAND_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BRAND_400', opacity: 1},
    [Themes.DARKER]: {raw: 'BRAND_360', opacity: 1},
  },
  TEXT_DANGER: {
    category: 'text',
    [Themes.DARK]: {raw: 'RED_345', opacity: 1},
    [Themes.LIGHT]: {raw: 'RED_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'RED_345', opacity: 1},
    [Themes.DARKER]: {raw: 'RED_345', opacity: 1},
  },
  TEXT_LINK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLUE_345', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLUE_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLUE_345', opacity: 1},
    [Themes.DARKER]: {raw: 'BLUE_345', opacity: 1},
  },
  TEXT_LINK_LOW_SATURATION: {
    category: 'generic',
    [Themes.DARK]: {raw: 'BLUE_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'BLUE_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLUE_330', opacity: 1},
    [Themes.DARKER]: {raw: 'BLUE_330', opacity: 1},
  },
  TEXT_LOW_CONTRAST: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_12', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  TEXT_MESSAGE_PREVIEW_LOW_SAT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_9', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 1},
  },
  TEXT_MUTED: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_460', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_10', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_10', opacity: 1},
  },
  TEXT_MUTED_ON_DEFAULT: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.DARKER]: {raw: 'PRIMARY_330', opacity: 1},
  },
  TEXT_NORMAL: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_4', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_6', opacity: 1},
  },
  TEXT_POSITIVE: {
    category: 'text',
    [Themes.DARK]: {raw: 'GREEN_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'GREEN_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'GREEN_330', opacity: 1},
    [Themes.DARKER]: {raw: 'GREEN_330', opacity: 1},
  },
  TEXT_PRIMARY: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_230', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_600', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_4', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_6', opacity: 1},
  },
  TEXT_SECONDARY: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_330', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_8', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_9', opacity: 1},
  },
  TEXT_WARNING: {
    category: 'text',
    [Themes.DARK]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.LIGHT]: {raw: 'YELLOW_430', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'YELLOW_300', opacity: 1},
    [Themes.DARKER]: {raw: 'YELLOW_300', opacity: 1},
  },
  TEXTBOX_MARKDOWN_SYNTAX: {
    category: 'text',
    [Themes.DARK]: {raw: 'PRIMARY_360', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_530', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_9', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_9', opacity: 1},
  },
  THEME_LOCKED_BLUR_FALLBACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.96},
    [Themes.LIGHT]: {raw: 'PRIMARY_700', opacity: 0.96},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 0.96},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 0.96},
  },
  THREAD_CHANNEL_SPINE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_300', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 1},
  },
  TOAST_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 1},
    gradient: {
      light: {color: 'gradient.start', lightness: 0.7, saturation: 0.5, opacity: 0.95},
      dark: {color: 'gradient.start', lightness: 0.4, saturation: 0.5, opacity: 0.95},
    },
  },
  TYPING_INDICATOR_BG: {
    category: 'background',
    [Themes.DARK]: {raw: 'PRIMARY_630', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_130', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'BLACK', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 1},
    gradient: {
      light: {color: 'gradient.end', lightness: '*0.9', saturation: '*0.9', opacity: 0.9},
      dark: {color: 'gradient.end', lightness: '*1.5', saturation: '*1.2', opacity: 0.9},
    },
  },
  USER_PROFILE_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 0.3},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 0.3},
  },
  USER_PROFILE_BORDER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 0.08},
    [Themes.LIGHT]: {raw: 'BLACK', opacity: 0.08},
    [Themes.MIDNIGHT]: {raw: 'PLUM_11', opacity: 0.14},
    [Themes.DARKER]: {raw: 'PLUM_11', opacity: 0.12},
  },
  USER_PROFILE_HEADER_OVERFLOW_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.5},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 0.5},
    [Themes.MIDNIGHT]: {raw: 'PLUM_20', opacity: 0.5},
    [Themes.DARKER]: {raw: 'PLUM_20', opacity: 0.5},
  },
  USER_PROFILE_NOTE_BACKGROUND_FOCUS: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 1},
    [Themes.LIGHT]: {raw: 'PRIMARY_200', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_19', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_16', opacity: 1},
  },
  USER_PROFILE_OVERLAY_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_800', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'PLUM_23', opacity: 1},
    [Themes.DARKER]: {raw: 'PLUM_18', opacity: 1},
  },
  USER_PROFILE_OVERLAY_BACKGROUND_HOVER: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_500', opacity: 0.3},
    [Themes.LIGHT]: {raw: 'PRIMARY_400', opacity: 0.16},
    [Themes.MIDNIGHT]: {raw: 'PLUM_13', opacity: 0.3},
    [Themes.DARKER]: {raw: 'PLUM_13', opacity: 0.3},
  },
  VOICE_VIDEO_VIDEO_TILE_BACKGROUND: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PLUM_17', opacity: 0.4},
    [Themes.LIGHT]: {raw: 'PLUM_17', opacity: 0.4},
    [Themes.MIDNIGHT]: {raw: 'PLUM_17', opacity: 0.4},
    [Themes.DARKER]: {raw: 'PLUM_17', opacity: 0.4},
  },
  VOICE_VIDEO_VIDEO_TILE_BLUR_FALLBACK: {
    category: 'generic',
    [Themes.DARK]: {raw: 'PRIMARY_700', opacity: 0.48},
    [Themes.LIGHT]: {raw: 'PRIMARY_700', opacity: 0.48},
    [Themes.MIDNIGHT]: {raw: 'PRIMARY_700', opacity: 0.48},
    [Themes.DARKER]: {raw: 'PRIMARY_700', opacity: 0.48},
  },
  WHITE: {
    category: 'generic',
    [Themes.DARK]: {raw: 'WHITE', opacity: 1},
    [Themes.LIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.MIDNIGHT]: {raw: 'WHITE', opacity: 1},
    [Themes.DARKER]: {raw: 'WHITE', opacity: 1},
  },
} satisfies SemanticColors;

const RawColors = {
  APPLICATION_SUBSCRIPTION_END: /* rgb(0.39% 47% 64%) */ '#0177a4',
  APPLICATION_SUBSCRIPTION_START: /* rgb(20% 26% 85%) */ '#3442d9',
  BATTLENET: /* rgb(0% 60% 90%) */ '#009ae5',
  BG_ANIMATED_GRADIENT_BACKGROUND_INDIGO_1: /* rgb(13% 12% 43%) */ '#201e6e',
  BG_ANIMATED_GRADIENT_BACKGROUND_INDIGO_2: /* rgb(11% 0% 40%) */ '#1d0066',
  BG_ANIMATED_GRADIENT_BACKGROUND_NOT_BLACK: /* rgb(5.1% 5.1% 5.9%) */ '#0d0d0f',
  BG_ANIMATED_GRADIENT_BACKGROUND_PINK_1: /* rgb(92% 27% 62%) */ '#eb459e',
  BG_GRADIENT_AURORA_1: /* rgb(2.4% 13% 33%) */ '#062053',
  BG_GRADIENT_AURORA_2: /* rgb(9.8% 12% 73%) */ '#191fbb',
  BG_GRADIENT_AURORA_3: /* rgb(7.5% 57% 60%) */ '#13929a',
  BG_GRADIENT_AURORA_4: /* rgb(13% 52% 45%) */ '#218573',
  BG_GRADIENT_AURORA_5: /* rgb(2% 10% 51%) */ '#051a81',
  BG_GRADIENT_BLURPLE_TWILIGHT_1: /* rgb(17% 25% 91%) */ '#2c3fe7',
  BG_GRADIENT_BLURPLE_TWILIGHT_2: /* rgb(15% 11% 51%) */ '#261d83',
  BG_GRADIENT_CHROMA_GLOW_1: /* rgb(5.5% 71% 75%) */ '#0eb5bf',
  BG_GRADIENT_CHROMA_GLOW_2: /* rgb(30% 4.7% 88%) */ '#4c0ce0',
  BG_GRADIENT_CHROMA_GLOW_3: /* rgb(64% 3.1% 65%) */ '#a308a7',
  BG_GRADIENT_CHROMA_GLOW_4: /* rgb(60% 33% 100%) */ '#9a53ff',
  BG_GRADIENT_CHROMA_GLOW_5: /* rgb(13% 55% 88%) */ '#218be0',
  BG_GRADIENT_CITRUS_SHERBERT_1: /* rgb(95% 70% 21%) */ '#f3b336',
  BG_GRADIENT_CITRUS_SHERBERT_2: /* rgb(93% 52% 35%) */ '#ee8558',
  BG_GRADIENT_COTTON_CANDY_1: /* rgb(96% 67% 72%) */ '#f4abb8',
  BG_GRADIENT_COTTON_CANDY_2: /* rgb(69% 76% 99%) */ '#b1c2fc',
  BG_GRADIENT_CRIMSON_MOON_1: /* rgb(58% 3.5% 3.5%) */ '#950909',
  BG_GRADIENT_CRIMSON_MOON_2: /* rgb(0% 0% 0%) */ '#000000',
  BG_GRADIENT_DESERT_KHAKI_1: /* rgb(91% 86% 82%) */ '#e7dbd0',
  BG_GRADIENT_DESERT_KHAKI_2: /* rgb(87% 82% 70%) */ '#dfd0b2',
  BG_GRADIENT_DESERT_KHAKI_3: /* rgb(88% 84% 64%) */ '#e0d6a3',
  BG_GRADIENT_DUSK_1: /* rgb(40% 31% 41%) */ '#665069',
  BG_GRADIENT_DUSK_2: /* rgb(57% 64% 82%) */ '#91a3d1',
  BG_GRADIENT_EASTER_EGG_1: /* rgb(45% 54% 85%) */ '#7289da',
  BG_GRADIENT_EASTER_EGG_2: /* rgb(31% 36% 58%) */ '#4e5d94',
  BG_GRADIENT_FOREST_1: /* rgb(7.8% 13% 8.2%) */ '#142215',
  BG_GRADIENT_FOREST_2: /* rgb(18% 30% 22%) */ '#2d4d39',
  BG_GRADIENT_FOREST_3: /* rgb(27% 30% 20%) */ '#454c32',
  BG_GRADIENT_FOREST_4: /* rgb(35% 49% 35%) */ '#5a7c58',
  BG_GRADIENT_FOREST_5: /* rgb(66% 56% 29%) */ '#a98e4b',
  BG_GRADIENT_HANAMI_1: /* rgb(94% 67% 70%) */ '#efaab3',
  BG_GRADIENT_HANAMI_2: /* rgb(94% 84% 59%) */ '#efd696',
  BG_GRADIENT_HANAMI_3: /* rgb(65% 85% 64%) */ '#a6daa2',
  BG_GRADIENT_LOFI_VIBES_1: /* rgb(64% 75% 97%) */ '#a4c0f7',
  BG_GRADIENT_LOFI_VIBES_2: /* rgb(66% 89% 91%) */ '#a9e4e8',
  BG_GRADIENT_LOFI_VIBES_3: /* rgb(69% 89% 72%) */ '#b0e2b8',
  BG_GRADIENT_LOFI_VIBES_4: /* rgb(81% 87% 64%) */ '#cfdfa2',
  BG_GRADIENT_MARS_1: /* rgb(54% 32% 25%) */ '#895240',
  BG_GRADIENT_MARS_2: /* rgb(56% 26% 26%) */ '#8f4343',
  BG_GRADIENT_MIDNIGHT_BLURPLE_1: /* rgb(33% 28% 79%) */ '#5348ca',
  BG_GRADIENT_MIDNIGHT_BLURPLE_2: /* rgb(7.8% 2.7% 19%) */ '#140730',
  BG_GRADIENT_MINT_APPLE_1: /* rgb(34% 71% 62%) */ '#56b69f',
  BG_GRADIENT_MINT_APPLE_2: /* rgb(39% 74% 38%) */ '#63bc61',
  BG_GRADIENT_MINT_APPLE_3: /* rgb(62% 79% 40%) */ '#9eca67',
  BG_GRADIENT_NEON_NIGHTS_1: /* rgb(0.39% 66% 62%) */ '#01a89e',
  BG_GRADIENT_NEON_NIGHTS_2: /* rgb(49% 38% 73%) */ '#7d60ba',
  BG_GRADIENT_NEON_NIGHTS_3: /* rgb(71% 22% 60%) */ '#b43898',
  BG_GRADIENT_RETRO_RAINCLOUD_1: /* rgb(23% 49% 63%) */ '#3a7ca1',
  BG_GRADIENT_RETRO_RAINCLOUD_2: /* rgb(50% 49% 73%) */ '#7f7eb9',
  BG_GRADIENT_RETRO_STORM_1: /* rgb(23% 49% 63%) */ '#3a7ca1',
  BG_GRADIENT_RETRO_STORM_2: /* rgb(35% 34% 60%) */ '#58579a',
  BG_GRADIENT_SEPIA_1: /* rgb(52% 46% 39%) */ '#857664',
  BG_GRADIENT_SEPIA_2: /* rgb(36% 27% 13%) */ '#5b4421',
  BG_GRADIENT_STRAWBERRY_LEMONADE_1: /* rgb(69% 10% 42%) */ '#af1a6c',
  BG_GRADIENT_STRAWBERRY_LEMONADE_2: /* rgb(76% 42% 13%) */ '#c26b20',
  BG_GRADIENT_STRAWBERRY_LEMONADE_3: /* rgb(91% 65% 15%) */ '#e7a525',
  BG_GRADIENT_SUNRISE_1: /* rgb(62% 25% 46%) */ '#9f4175',
  BG_GRADIENT_SUNRISE_2: /* rgb(77% 56% 39%) */ '#c49064',
  BG_GRADIENT_SUNRISE_3: /* rgb(65% 58% 24%) */ '#a6953d',
  BG_GRADIENT_SUNSET_1: /* rgb(28% 16% 55%) */ '#48288c',
  BG_GRADIENT_SUNSET_2: /* rgb(86% 50% 29%) */ '#db7f4b',
  BG_GRADIENT_UNDER_THE_SEA_1: /* rgb(39% 47% 38%) */ '#647962',
  BG_GRADIENT_UNDER_THE_SEA_2: /* rgb(35% 52% 46%) */ '#588575',
  BG_GRADIENT_UNDER_THE_SEA_3: /* rgb(42% 52% 51%) */ '#6a8482',
  BG_MODAL_GRADIENT_GREEN_DARK_1: /* rgb(13% 73% 39%) */ '#20bb63',
  BG_MODAL_GRADIENT_GREEN_DARK_2: /* rgb(0% 40% 22%) */ '#006638',
  BG_MODAL_GRADIENT_GREEN_LIGHT_1: /* rgb(90% 100% 94%) */ '#e5fff0',
  BG_MODAL_GRADIENT_GREEN_LIGHT_2: /* rgb(54% 97% 71%) */ '#8af7b5',
  BG_MODAL_GRADIENT_LAVENDER_DARK_1: /* rgb(63% 34% 95%) */ '#a056f2',
  BG_MODAL_GRADIENT_LAVENDER_DARK_2: /* rgb(24% 8.6% 54%) */ '#3e1689',
  BG_MODAL_GRADIENT_LAVENDER_LIGHT_1: /* rgb(87% 76% 99%) */ '#dec2fc',
  BG_MODAL_GRADIENT_LAVENDER_LIGHT_2: /* rgb(79% 62% 98%) */ '#ca9ef9',
  BG_MODAL_GRADIENT_MAGENTA_DARK_1: /* rgb(70% 15% 61%) */ '#b3269c',
  BG_MODAL_GRADIENT_MAGENTA_DARK_2: /* rgb(40% 0% 40%) */ '#660066',
  BG_MODAL_GRADIENT_MAGENTA_LIGHT_1: /* rgb(98% 90% 100%) */ '#fbe6ff',
  BG_MODAL_GRADIENT_MAGENTA_LIGHT_2: /* rgb(99% 60% 91%) */ '#fd99e9',
  BG_MODAL_GRADIENT_NAVY_DARK_1: /* rgb(35% 40% 95%) */ '#5865f2',
  BG_MODAL_GRADIENT_NAVY_DARK_2: /* rgb(8.6% 10% 54%) */ '#161a8a',
  BG_MODAL_GRADIENT_NAVY_LIGHT_1: /* rgb(76% 78% 99%) */ '#c3c8fc',
  BG_MODAL_GRADIENT_NAVY_LIGHT_2: /* rgb(63% 66% 98%) */ '#a0a9fa',
  BG_TILE_GRADIENT_MAGENTA_DARK_1: /* rgb(70% 15% 61%) */ '#b3269c',
  BG_TILE_GRADIENT_MAGENTA_DARK_2: /* rgb(40% 0% 40%) */ '#660066',
  BLACK: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_100: /* rgb(95% 95% 95%) */ '#f2f2f2',
  BLACK_130: /* rgb(91% 91% 91%) */ '#e8e8e8',
  BLACK_160: /* rgb(85% 85% 85%) */ '#dadada',
  BLACK_200: /* rgb(80% 80% 80%) */ '#cccccc',
  BLACK_230: /* rgb(74% 74% 74%) */ '#bdbdbd',
  BLACK_260: /* rgb(67% 67% 67%) */ '#acacac',
  BLACK_300: /* rgb(60% 60% 60%) */ '#999999',
  BLACK_330: /* rgb(48% 48% 48%) */ '#7a7a7a',
  BLACK_345: /* rgb(40% 40% 40%) */ '#666666',
  BLACK_360: /* rgb(36% 36% 36%) */ '#5c5c5c',
  BLACK_400: /* rgb(20% 20% 20%) */ '#333333',
  BLACK_430: /* rgb(15% 15% 15%) */ '#252525',
  BLACK_460: /* rgb(7.8% 7.8% 7.8%) */ '#141414',
  BLACK_500: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_530: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_560: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_600: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_630: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_660: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_700: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_730: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_760: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_800: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_830: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_860: /* rgb(0% 0% 0%) */ '#000000',
  BLACK_900: /* rgb(0% 0% 0%) */ '#000000',
  BLUE_100: /* rgb(96% 98% 100%) */ '#f6fafe',
  BLUE_130: /* rgb(94% 97% 100%) */ '#f0f7fe',
  BLUE_160: /* rgb(89% 94% 99%) */ '#e2f0fd',
  BLUE_200: /* rgb(80% 91% 99%) */ '#cde8fd',
  BLUE_230: /* rgb(70% 87% 99%) */ '#b2ddfc',
  BLUE_260: /* rgb(58% 82% 99%) */ '#94d2fc',
  BLUE_300: /* rgb(40% 77% 99%) */ '#66c4fd',
  BLUE_330: /* rgb(18% 71% 100%) */ '#2eb6ff',
  BLUE_345: /* rgb(0% 67% 99%) */ '#00aafc',
  BLUE_360: /* rgb(0% 59% 95%) */ '#0097f2',
  BLUE_400: /* rgb(0% 51% 92%) */ '#0082eb',
  BLUE_430: /* rgb(0% 42% 91%) */ '#006be7',
  BLUE_460: /* rgb(0% 36% 82%) */ '#005cd1',
  BLUE_500: /* rgb(0% 32% 71%) */ '#0051b6',
  BLUE_530: /* rgb(0% 28% 61%) */ '#00489b',
  BLUE_560: /* rgb(0% 25% 53%) */ '#004088',
  BLUE_600: /* rgb(0% 22% 46%) */ '#003976',
  BLUE_630: /* rgb(0% 20% 42%) */ '#00336a',
  BLUE_660: /* rgb(0% 18% 37%) */ '#002d5f',
  BLUE_700: /* rgb(0% 16% 33%) */ '#002855',
  BLUE_730: /* rgb(0% 14% 28%) */ '#002348',
  BLUE_760: /* rgb(0% 12% 25%) */ '#001e3f',
  BLUE_800: /* rgb(0% 10% 21%) */ '#001a36',
  BLUE_830: /* rgb(0% 8.6% 19%) */ '#001630',
  BLUE_860: /* rgb(0% 7.5% 17%) */ '#00132b',
  BLUE_900: /* rgb(0% 6.3% 14%) */ '#001024',
  BLUE_NEW_24: /* rgb(47% 71% 100%) */ '#79b6ff',
  BLUE_NEW_26: /* rgb(45% 69% 99%) */ '#72b1fc',
  BLUE_NEW_34: /* rgb(33% 61% 95%) */ '#549cf3',
  BLUE_NEW_36: /* rgb(30% 59% 94%) */ '#4c97f0',
  BLUE_NEW_40: /* rgb(24% 55% 93%) */ '#3c8cec',
  BLUE_NEW_42: /* rgb(20% 53% 92%) */ '#3387ea',
  BLUE_NEW_49: /* rgb(0% 45% 89%) */ '#0074e3',
  BLUE_NEW_56: /* rgb(2.4% 40% 77%) */ '#0666c4',
  BLURPLE_14: /* rgb(71% 78% 100%) */ '#b4c7ff',
  BLURPLE_18: /* rgb(65% 74% 100%) */ '#a7bcff',
  BLURPLE_2: /* rgb(88% 90% 100%) */ '#e1e6ff',
  BLURPLE_21: /* rgb(62% 71% 100%) */ '#9eb4ff',
  BLURPLE_25: /* rgb(57% 66% 100%) */ '#92a9ff',
  BLURPLE_26: /* rgb(56% 65% 100%) */ '#90a6ff',
  BLURPLE_35: /* rgb(47% 56% 99%) */ '#788efd',
  BLURPLE_41: /* rgb(42% 49% 97%) */ '#6a7df7',
  BLURPLE_49: /* rgb(35% 40% 95%) */ '#5865f2',
  BLURPLE_50: /* rgb(35% 40% 95%) */ '#5865f2',
  BLURPLE_71: /* rgb(18% 24% 54%) */ '#2e3c89',
  BLURPLE_75: /* rgb(15% 21% 47%) */ '#273578',
  BLURPLE_8: /* rgb(79% 84% 100%) */ '#cad7ff',
  BRAND_100: /* rgb(97% 97% 100%) */ '#f7f7fe',
  BRAND_130: /* rgb(94% 95% 100%) */ '#f0f1fe',
  BRAND_160: /* rgb(91% 91% 99%) */ '#e7e9fd',
  BRAND_200: /* rgb(87% 88% 99%) */ '#dee0fc',
  BRAND_230: /* rgb(83% 84% 99%) */ '#d4d7fc',
  BRAND_260: /* rgb(79% 80% 98%) */ '#c9cdfb',
  BRAND_300: /* rgb(74% 76% 98%) */ '#bcc1fa',
  BRAND_330: /* rgb(66% 69% 97%) */ '#a8aff8',
  BRAND_345: /* rgb(61% 64% 97%) */ '#9ba3f7',
  BRAND_360: /* rgb(58% 61% 97%) */ '#949cf7',
  BRAND_400: /* rgb(47% 52% 96%) */ '#7984f5',
  BRAND_430: /* rgb(44% 48% 96%) */ '#707bf4',
  BRAND_460: /* rgb(40% 44% 95%) */ '#6571f3',
  BRAND_500: /* rgb(35% 40% 95%) */ '#5865f2',
  BRAND_530: /* rgb(31% 36% 86%) */ '#505cdc',
  BRAND_560: /* rgb(28% 32% 77%) */ '#4752c4',
  BRAND_600: /* rgb(24% 27% 65%) */ '#3c45a5',
  BRAND_630: /* rgb(20% 23% 56%) */ '#343b8f',
  BRAND_660: /* rgb(18% 20% 49%) */ '#2d347d',
  BRAND_700: /* rgb(14% 16% 38%) */ '#232861',
  BRAND_730: /* rgb(13% 15% 36%) */ '#21265b',
  BRAND_760: /* rgb(12% 14% 33%) */ '#1e2353',
  BRAND_800: /* rgb(10% 12% 29%) */ '#1a1e49',
  BRAND_830: /* rgb(7.8% 9% 22%) */ '#141738',
  BRAND_860: /* rgb(5.1% 5.9% 14%) */ '#0d0f24',
  BRAND_900: /* rgb(1.6% 2% 4.7%) */ '#04050c',
  BUNGIE: /* rgb(0% 64% 89%) */ '#00a3e3',
  CRUNCHYROLL: /* rgb(97% 55% 14%) */ '#f78b24',
  EBAY: /* rgb(0% 39% 82%) */ '#0064d2',
  EPIC_GAMES: /* rgb(24% 22% 21%) */ '#3c3935',
  FACEBOOK: /* rgb(21% 31% 54%) */ '#355089',
  GITHUB: /* rgb(9.8% 9% 9%) */ '#191717',
  GOLD: /* rgb(100% 78% 9.8%) */ '#ffc819',
  GREEN_100: /* rgb(93% 100% 95%) */ '#ecfef1',
  GREEN_130: /* rgb(87% 100% 91%) */ '#defee7',
  GREEN_160: /* rgb(74% 99% 83%) */ '#bdfcd3',
  GREEN_200: /* rgb(53% 98% 71%) */ '#88fbb5',
  GREEN_230: /* rgb(35% 95% 61%) */ '#58f39c',
  GREEN_260: /* rgb(28% 90% 55%) */ '#48e58b',
  GREEN_300: /* rgb(23% 84% 50%) */ '#3bd67f',
  GREEN_330: /* rgb(18% 78% 44%) */ '#2dc771',
  GREEN_345: /* rgb(15% 73% 41%) */ '#26b968',
  GREEN_360: /* rgb(14% 65% 35%) */ '#23a55a',
  GREEN_400: /* rgb(14% 58% 31%) */ '#24934e',
  GREEN_430: /* rgb(14% 50% 27%) */ '#248045',
  GREEN_460: /* rgb(12% 44% 24%) */ '#1f703c',
  GREEN_500: /* rgb(10% 39% 20%) */ '#1a6334',
  GREEN_530: /* rgb(8.2% 34% 17%) */ '#15562b',
  GREEN_560: /* rgb(7.1% 30% 14%) */ '#124c24',
  GREEN_600: /* rgb(5.5% 26% 12%) */ '#0e431f',
  GREEN_630: /* rgb(4.7% 24% 11%) */ '#0c3c1c',
  GREEN_660: /* rgb(3.9% 21% 9.4%) */ '#0a3618',
  GREEN_700: /* rgb(2.7% 18% 8.2%) */ '#072f15',
  GREEN_730: /* rgb(2% 16% 6.3%) */ '#052910',
  GREEN_760: /* rgb(1.2% 14% 5.5%) */ '#03240e',
  GREEN_800: /* rgb(1.2% 12% 4.7%) */ '#031f0c',
  GREEN_830: /* rgb(1.2% 11% 3.9%) */ '#031b0a',
  GREEN_860: /* rgb(1.6% 9% 3.1%) */ '#041708',
  GREEN_900: /* rgb(2% 7.5% 2.7%) */ '#051307',
  GREEN_NEW_24: /* rgb(43% 78% 50%) */ '#6ec77f',
  GREEN_NEW_34: /* rgb(31% 67% 39%) */ '#4eac63',
  GREEN_NEW_40: /* rgb(22% 62% 33%) */ '#399d53',
  GREEN_NEW_50: /* rgb(0% 53% 23%) */ '#00863a',
  GREEN_NEW_53: /* rgb(1.6% 49% 22%) */ '#047e37',
  GREEN_NEW_55: /* rgb(2% 47% 20%) */ '#057834',
  GREEN_NEW_57: /* rgb(2% 45% 20%) */ '#057332',
  GUILD_BOOSTING_BLUE: /* rgb(24% 44% 87%) */ '#3e70dd',
  GUILD_BOOSTING_BLUE_FOR_GRADIENTS: /* rgb(20% 26% 85%) */ '#3442d9',
  GUILD_BOOSTING_PINK: /* rgb(100% 45% 98%) */ '#ff73fa',
  GUILD_BOOSTING_PURPLE: /* rgb(70% 47% 95%) */ '#b377f3',
  GUILD_BOOSTING_PURPLE_FOR_GRADIENTS: /* rgb(61% 27% 97%) */ '#9b44f7',
  HYPESQUAD_HOUSE_1: /* rgb(61% 52% 94%) */ '#9c84ef',
  HYPESQUAD_HOUSE_2: /* rgb(96% 48% 40%) */ '#f47b67',
  HYPESQUAD_HOUSE_3: /* rgb(27% 87% 75%) */ '#45ddc0',
  INPUT_PLACEHOLDER_TEXT_DARK: /* rgb(53% 54% 55%) */ '#87898c',
  LOL: /* rgb(0.78% 12% 15%) */ '#021f25',
  LOL_TEXT_DARK: /* rgb(58% 38% 5.9%) */ '#94620f',
  LOL_TEXT_LIGHT: /* rgb(80% 67% 38%) */ '#cbaa62',
  NEUTRAL_1: /* rgb(100% 100% 100%) */ '#ffffff',
  NEUTRAL_10: /* rgb(86% 86% 87%) */ '#dbdcdf',
  NEUTRAL_100: /* rgb(0% 0% 0%) */ '#000000',
  NEUTRAL_15: /* rgb(78% 78% 80%) */ '#c8c8cd',
  NEUTRAL_16: /* rgb(77% 77% 79%) */ '#c4c5c9',
  NEUTRAL_17: /* rgb(75% 76% 78%) */ '#c0c1c6',
  NEUTRAL_2: /* rgb(98% 98% 98%) */ '#fbfbfb',
  NEUTRAL_22: /* rgb(68% 68% 71%) */ '#adaeb4',
  NEUTRAL_23: /* rgb(67% 67% 69%) */ '#aaaab1',
  NEUTRAL_27: /* rgb(61% 61% 64%) */ '#9b9ca3',
  NEUTRAL_29: /* rgb(58% 58% 61%) */ '#94959c',
  NEUTRAL_3: /* rgb(97% 97% 97%) */ '#f7f7f8',
  NEUTRAL_30: /* rgb(56% 57% 60%) */ '#909199',
  NEUTRAL_31: /* rgb(55% 55% 58%) */ '#8c8d95',
  NEUTRAL_32: /* rgb(54% 54% 57%) */ '#898a92',
  NEUTRAL_33: /* rgb(52% 53% 56%) */ '#85868e',
  NEUTRAL_34: /* rgb(51% 51% 55%) */ '#82838b',
  NEUTRAL_36: /* rgb(48% 49% 52%) */ '#7b7c84',
  NEUTRAL_38: /* rgb(45% 46% 49%) */ '#74757e',
  NEUTRAL_4: /* rgb(95% 95% 96%) */ '#f3f3f4',
  NEUTRAL_42: /* rgb(40% 40% 44%) */ '#666771',
  NEUTRAL_43: /* rgb(39% 39% 43%) */ '#63646d',
  NEUTRAL_44: /* rgb(37% 38% 42%) */ '#5f606a',
  NEUTRAL_45: /* rgb(36% 36% 40%) */ '#5c5d67',
  NEUTRAL_46: /* rgb(35% 35% 39%) */ '#595a64',
  NEUTRAL_5: /* rgb(94% 94% 94%) */ '#efeff0',
  NEUTRAL_59: /* rgb(25% 26% 29%) */ '#41424a',
  NEUTRAL_6: /* rgb(92% 92% 93%) */ '#ebebed',
  NEUTRAL_60: /* rgb(25% 25% 28%) */ '#3f4048',
  NEUTRAL_64: /* rgb(22% 23% 25%) */ '#393a41',
  NEUTRAL_66: /* rgb(21% 22% 24%) */ '#36373e',
  NEUTRAL_69: /* rgb(20% 20% 22%) */ '#323339',
  NEUTRAL_71: /* rgb(18% 19% 21%) */ '#2f3035',
  NEUTRAL_73: /* rgb(17% 18% 20%) */ '#2c2d32',
  NEUTRAL_74: /* rgb(17% 17% 19%) */ '#2b2b31',
  NEUTRAL_79: /* rgb(14% 14% 16%) */ '#242429',
  NEUTRAL_8: /* rgb(89% 89% 90%) */ '#e3e3e6',
  NEUTRAL_82: /* rgb(13% 13% 14%) */ '#202024',
  NEUTRAL_83: /* rgb(12% 12% 13%) */ '#1e1f22',
  NEUTRAL_86: /* rgb(10% 10% 12%) */ '#1a1a1e',
  NEUTRAL_9: /* rgb(87% 88% 89%) */ '#dfe0e2',
  NEUTRAL_90: /* rgb(8.2% 8.2% 9.4%) */ '#151518',
  NEUTRAL_91: /* rgb(7.5% 7.8% 8.6%) */ '#131416',
  NEUTRAL_92: /* rgb(7.1% 7.1% 7.8%) */ '#121214',
  NEUTRAL_95: /* rgb(4.7% 4.7% 5.5%) */ '#0c0c0e',
  NEUTRAL_97: /* rgb(2.7% 2.7% 3.5%) */ '#070709',
  NEUTRAL_98: /* rgb(2% 2% 2.4%) */ '#050506',
  OPACITY_1: /* rgb(59% 59% 62% / 0.01) */ '#97979f03',
  OPACITY_12: /* rgb(59% 59% 62% / 0.12) */ '#97979f1f',
  OPACITY_16: /* rgb(59% 59% 62% / 0.16) */ '#97979f29',
  OPACITY_20: /* rgb(59% 59% 62% / 0.2) */ '#97979f33',
  OPACITY_24: /* rgb(59% 59% 62% / 0.24) */ '#97979f3d',
  OPACITY_28: /* rgb(59% 59% 62% / 0.28) */ '#97979f47',
  OPACITY_32: /* rgb(59% 59% 62% / 0.32) */ '#97979f52',
  OPACITY_36: /* rgb(59% 59% 62% / 0.36) */ '#97979f5c',
  OPACITY_4: /* rgb(59% 59% 62% / 0.04) */ '#97979f0a',
  OPACITY_40: /* rgb(59% 59% 62% / 0.4) */ '#97979f66',
  OPACITY_44: /* rgb(59% 59% 62% / 0.44) */ '#97979f70',
  OPACITY_48: /* rgb(59% 59% 62% / 0.48) */ '#97979f7a',
  OPACITY_52: /* rgb(59% 59% 62% / 0.52) */ '#97979f85',
  OPACITY_56: /* rgb(59% 59% 62% / 0.56) */ '#97979f8f',
  OPACITY_60: /* rgb(59% 59% 62% / 0.6) */ '#97979f99',
  OPACITY_64: /* rgb(59% 59% 62% / 0.64) */ '#97979fa3',
  OPACITY_68: /* rgb(59% 59% 62% / 0.68) */ '#97979fad',
  OPACITY_72: /* rgb(59% 59% 62% / 0.72) */ '#97979fb8',
  OPACITY_76: /* rgb(59% 59% 62% / 0.76) */ '#97979fc2',
  OPACITY_8: /* rgb(59% 59% 62% / 0.08) */ '#97979f14',
  OPACITY_80: /* rgb(59% 59% 62% / 0.8) */ '#97979fcc',
  OPACITY_84: /* rgb(59% 59% 62% / 0.84) */ '#97979fd6',
  OPACITY_88: /* rgb(59% 59% 62% / 0.88) */ '#97979fe0',
  OPACITY_92: /* rgb(59% 59% 62% / 0.92) */ '#97979feb',
  OPACITY_96: /* rgb(59% 59% 62% / 0.96) */ '#97979ff5',
  OPACITY_BLACK_1: /* rgb(0% 0% 0% / 0.01) */ '#00000003',
  OPACITY_BLACK_12: /* rgb(0% 0% 0% / 0.12) */ '#0000001f',
  OPACITY_BLACK_16: /* rgb(0% 0% 0% / 0.16) */ '#00000029',
  OPACITY_BLACK_20: /* rgb(0% 0% 0% / 0.2) */ '#00000033',
  OPACITY_BLACK_24: /* rgb(0% 0% 0% / 0.24) */ '#0000003d',
  OPACITY_BLACK_28: /* rgb(0% 0% 0% / 0.28) */ '#00000047',
  OPACITY_BLACK_32: /* rgb(0% 0% 0% / 0.32) */ '#00000052',
  OPACITY_BLACK_36: /* rgb(0% 0% 0% / 0.36) */ '#0000005c',
  OPACITY_BLACK_4: /* rgb(0% 0% 0% / 0.04) */ '#0000000a',
  OPACITY_BLACK_40: /* rgb(0% 0% 0% / 0.4) */ '#00000066',
  OPACITY_BLACK_44: /* rgb(0% 0% 0% / 0.44) */ '#00000070',
  OPACITY_BLACK_48: /* rgb(0% 0% 0% / 0.48) */ '#0000007a',
  OPACITY_BLACK_52: /* rgb(0% 0% 0% / 0.52) */ '#00000085',
  OPACITY_BLACK_56: /* rgb(0% 0% 0% / 0.56) */ '#0000008f',
  OPACITY_BLACK_60: /* rgb(0% 0% 0% / 0.6) */ '#00000099',
  OPACITY_BLACK_64: /* rgb(0% 0% 0% / 0.64) */ '#000000a3',
  OPACITY_BLACK_68: /* rgb(0% 0% 0% / 0.68) */ '#000000ad',
  OPACITY_BLACK_72: /* rgb(0% 0% 0% / 0.72) */ '#000000b8',
  OPACITY_BLACK_76: /* rgb(0% 0% 0% / 0.76) */ '#000000c2',
  OPACITY_BLACK_8: /* rgb(0% 0% 0% / 0.08) */ '#00000014',
  OPACITY_BLACK_80: /* rgb(0% 0% 0% / 0.8) */ '#000000cc',
  OPACITY_BLACK_84: /* rgb(0% 0% 0% / 0.84) */ '#000000d6',
  OPACITY_BLACK_88: /* rgb(0% 0% 0% / 0.88) */ '#000000e0',
  OPACITY_BLACK_92: /* rgb(0% 0% 0% / 0.92) */ '#000000eb',
  OPACITY_BLACK_96: /* rgb(0% 0% 0% / 0.96) */ '#000000f5',
  OPACITY_BLUE_1: /* rgb(0% 45% 89% / 0.01) */ '#0074e303',
  OPACITY_BLUE_12: /* rgb(0% 45% 89% / 0.12) */ '#0074e31f',
  OPACITY_BLUE_16: /* rgb(0% 45% 89% / 0.16) */ '#0074e329',
  OPACITY_BLUE_20: /* rgb(0% 45% 89% / 0.2) */ '#0074e333',
  OPACITY_BLUE_24: /* rgb(0% 45% 89% / 0.24) */ '#0074e33d',
  OPACITY_BLUE_28: /* rgb(0% 45% 89% / 0.28) */ '#0074e347',
  OPACITY_BLUE_32: /* rgb(0% 45% 89% / 0.32) */ '#0074e352',
  OPACITY_BLUE_36: /* rgb(0% 45% 89% / 0.36) */ '#0074e35c',
  OPACITY_BLUE_4: /* rgb(0% 45% 89% / 0.04) */ '#0074e30a',
  OPACITY_BLUE_40: /* rgb(0% 45% 89% / 0.4) */ '#0074e366',
  OPACITY_BLUE_44: /* rgb(0% 45% 89% / 0.44) */ '#0074e370',
  OPACITY_BLUE_48: /* rgb(0% 45% 89% / 0.48) */ '#0074e37a',
  OPACITY_BLUE_52: /* rgb(0% 45% 89% / 0.52) */ '#0074e385',
  OPACITY_BLUE_56: /* rgb(0% 45% 89% / 0.56) */ '#0074e38f',
  OPACITY_BLUE_60: /* rgb(0% 45% 89% / 0.6) */ '#0074e399',
  OPACITY_BLUE_64: /* rgb(0% 45% 89% / 0.64) */ '#0074e3a3',
  OPACITY_BLUE_68: /* rgb(0% 45% 89% / 0.68) */ '#0074e3ad',
  OPACITY_BLUE_72: /* rgb(0% 45% 89% / 0.72) */ '#0074e3b8',
  OPACITY_BLUE_76: /* rgb(0% 45% 89% / 0.76) */ '#0074e3c2',
  OPACITY_BLUE_8: /* rgb(0% 45% 89% / 0.08) */ '#0074e314',
  OPACITY_BLUE_80: /* rgb(0% 45% 89% / 0.8) */ '#0074e3cc',
  OPACITY_BLUE_84: /* rgb(0% 45% 89% / 0.84) */ '#0074e3d6',
  OPACITY_BLUE_88: /* rgb(0% 45% 89% / 0.88) */ '#0074e3e0',
  OPACITY_BLUE_92: /* rgb(0% 45% 89% / 0.92) */ '#0074e3eb',
  OPACITY_BLUE_96: /* rgb(0% 45% 89% / 0.96) */ '#0074e3f5',
  OPACITY_BLURPLE_1: /* rgb(35% 40% 95% / 0.01) */ '#5865f203',
  OPACITY_BLURPLE_12: /* rgb(35% 40% 95% / 0.12) */ '#5865f21f',
  OPACITY_BLURPLE_16: /* rgb(35% 40% 95% / 0.16) */ '#5865f229',
  OPACITY_BLURPLE_20: /* rgb(35% 40% 95% / 0.2) */ '#5865f233',
  OPACITY_BLURPLE_24: /* rgb(35% 40% 95% / 0.24) */ '#5865f23d',
  OPACITY_BLURPLE_28: /* rgb(35% 40% 95% / 0.28) */ '#5865f247',
  OPACITY_BLURPLE_32: /* rgb(35% 40% 95% / 0.32) */ '#5865f252',
  OPACITY_BLURPLE_36: /* rgb(35% 40% 95% / 0.36) */ '#5865f25c',
  OPACITY_BLURPLE_4: /* rgb(35% 40% 95% / 0.04) */ '#5865f20a',
  OPACITY_BLURPLE_40: /* rgb(35% 40% 95% / 0.4) */ '#5865f266',
  OPACITY_BLURPLE_44: /* rgb(35% 40% 95% / 0.44) */ '#5865f270',
  OPACITY_BLURPLE_48: /* rgb(35% 40% 95% / 0.48) */ '#5865f27a',
  OPACITY_BLURPLE_52: /* rgb(35% 40% 95% / 0.52) */ '#5865f285',
  OPACITY_BLURPLE_56: /* rgb(35% 40% 95% / 0.56) */ '#5865f28f',
  OPACITY_BLURPLE_60: /* rgb(35% 40% 95% / 0.6) */ '#5865f299',
  OPACITY_BLURPLE_64: /* rgb(35% 40% 95% / 0.64) */ '#5865f2a3',
  OPACITY_BLURPLE_68: /* rgb(35% 40% 95% / 0.68) */ '#5865f2ad',
  OPACITY_BLURPLE_72: /* rgb(35% 40% 95% / 0.72) */ '#5865f2b8',
  OPACITY_BLURPLE_76: /* rgb(35% 40% 95% / 0.76) */ '#5865f2c2',
  OPACITY_BLURPLE_8: /* rgb(35% 40% 95% / 0.08) */ '#5865f214',
  OPACITY_BLURPLE_80: /* rgb(35% 40% 95% / 0.8) */ '#5865f2cc',
  OPACITY_BLURPLE_84: /* rgb(35% 40% 95% / 0.84) */ '#5865f2d6',
  OPACITY_BLURPLE_88: /* rgb(35% 40% 95% / 0.88) */ '#5865f2e0',
  OPACITY_BLURPLE_92: /* rgb(35% 40% 95% / 0.92) */ '#5865f2eb',
  OPACITY_BLURPLE_96: /* rgb(35% 40% 95% / 0.96) */ '#5865f2f5',
  OPACITY_GREEN_1: /* rgb(0% 53% 23% / 0.01) */ '#00863a03',
  OPACITY_GREEN_12: /* rgb(0% 53% 23% / 0.12) */ '#00863a1f',
  OPACITY_GREEN_16: /* rgb(0% 53% 23% / 0.16) */ '#00863a29',
  OPACITY_GREEN_20: /* rgb(0% 53% 23% / 0.2) */ '#00863a33',
  OPACITY_GREEN_24: /* rgb(0% 53% 23% / 0.24) */ '#00863a3d',
  OPACITY_GREEN_28: /* rgb(0% 53% 23% / 0.28) */ '#00863a47',
  OPACITY_GREEN_32: /* rgb(0% 53% 23% / 0.32) */ '#00863a52',
  OPACITY_GREEN_36: /* rgb(0% 53% 23% / 0.36) */ '#00863a5c',
  OPACITY_GREEN_4: /* rgb(0% 53% 23% / 0.04) */ '#00863a0a',
  OPACITY_GREEN_40: /* rgb(0% 53% 23% / 0.4) */ '#00863a66',
  OPACITY_GREEN_44: /* rgb(0% 53% 23% / 0.44) */ '#00863a70',
  OPACITY_GREEN_48: /* rgb(0% 53% 23% / 0.48) */ '#00863a7a',
  OPACITY_GREEN_52: /* rgb(0% 53% 23% / 0.52) */ '#00863a85',
  OPACITY_GREEN_56: /* rgb(0% 53% 23% / 0.56) */ '#00863a8f',
  OPACITY_GREEN_60: /* rgb(0% 53% 23% / 0.6) */ '#00863a99',
  OPACITY_GREEN_64: /* rgb(0% 53% 23% / 0.64) */ '#00863aa3',
  OPACITY_GREEN_68: /* rgb(0% 53% 23% / 0.68) */ '#00863aad',
  OPACITY_GREEN_72: /* rgb(0% 53% 23% / 0.72) */ '#00863ab8',
  OPACITY_GREEN_76: /* rgb(0% 53% 23% / 0.76) */ '#00863ac2',
  OPACITY_GREEN_8: /* rgb(0% 53% 23% / 0.08) */ '#00863a14',
  OPACITY_GREEN_80: /* rgb(0% 53% 23% / 0.8) */ '#00863acc',
  OPACITY_GREEN_84: /* rgb(0% 53% 23% / 0.84) */ '#00863ad6',
  OPACITY_GREEN_88: /* rgb(0% 53% 23% / 0.88) */ '#00863ae0',
  OPACITY_GREEN_92: /* rgb(0% 53% 23% / 0.92) */ '#00863aeb',
  OPACITY_GREEN_96: /* rgb(0% 53% 23% / 0.96) */ '#00863af5',
  OPACITY_ORANGE_1: /* rgb(74% 31% 0% / 0.01) */ '#bd4f0003',
  OPACITY_ORANGE_12: /* rgb(74% 31% 0% / 0.12) */ '#bd4f001f',
  OPACITY_ORANGE_16: /* rgb(74% 31% 0% / 0.16) */ '#bd4f0029',
  OPACITY_ORANGE_20: /* rgb(74% 31% 0% / 0.2) */ '#bd4f0033',
  OPACITY_ORANGE_24: /* rgb(74% 31% 0% / 0.24) */ '#bd4f003d',
  OPACITY_ORANGE_28: /* rgb(74% 31% 0% / 0.28) */ '#bd4f0047',
  OPACITY_ORANGE_32: /* rgb(74% 31% 0% / 0.32) */ '#bd4f0052',
  OPACITY_ORANGE_36: /* rgb(74% 31% 0% / 0.36) */ '#bd4f005c',
  OPACITY_ORANGE_4: /* rgb(74% 31% 0% / 0.04) */ '#bd4f000a',
  OPACITY_ORANGE_40: /* rgb(74% 31% 0% / 0.4) */ '#bd4f0066',
  OPACITY_ORANGE_44: /* rgb(74% 31% 0% / 0.44) */ '#bd4f0070',
  OPACITY_ORANGE_48: /* rgb(74% 31% 0% / 0.48) */ '#bd4f007a',
  OPACITY_ORANGE_52: /* rgb(74% 31% 0% / 0.52) */ '#bd4f0085',
  OPACITY_ORANGE_56: /* rgb(74% 31% 0% / 0.56) */ '#bd4f008f',
  OPACITY_ORANGE_60: /* rgb(74% 31% 0% / 0.6) */ '#bd4f0099',
  OPACITY_ORANGE_64: /* rgb(74% 31% 0% / 0.64) */ '#bd4f00a3',
  OPACITY_ORANGE_68: /* rgb(74% 31% 0% / 0.68) */ '#bd4f00ad',
  OPACITY_ORANGE_72: /* rgb(74% 31% 0% / 0.72) */ '#bd4f00b8',
  OPACITY_ORANGE_76: /* rgb(74% 31% 0% / 0.76) */ '#bd4f00c2',
  OPACITY_ORANGE_8: /* rgb(74% 31% 0% / 0.08) */ '#bd4f0014',
  OPACITY_ORANGE_80: /* rgb(74% 31% 0% / 0.8) */ '#bd4f00cc',
  OPACITY_ORANGE_84: /* rgb(74% 31% 0% / 0.84) */ '#bd4f00d6',
  OPACITY_ORANGE_88: /* rgb(74% 31% 0% / 0.88) */ '#bd4f00e0',
  OPACITY_ORANGE_92: /* rgb(74% 31% 0% / 0.92) */ '#bd4f00eb',
  OPACITY_ORANGE_96: /* rgb(74% 31% 0% / 0.96) */ '#bd4f00f5',
  OPACITY_PINK_1: /* rgb(100% 30% 82% / 0.01) */ '#ff4cd203',
  OPACITY_PINK_12: /* rgb(100% 30% 82% / 0.12) */ '#ff4cd21f',
  OPACITY_PINK_16: /* rgb(100% 30% 82% / 0.16) */ '#ff4cd229',
  OPACITY_PINK_20: /* rgb(100% 30% 82% / 0.2) */ '#ff4cd233',
  OPACITY_PINK_24: /* rgb(100% 30% 82% / 0.24) */ '#ff4cd23d',
  OPACITY_PINK_28: /* rgb(100% 30% 82% / 0.28) */ '#ff4cd247',
  OPACITY_PINK_32: /* rgb(100% 30% 82% / 0.32) */ '#ff4cd252',
  OPACITY_PINK_36: /* rgb(100% 30% 82% / 0.36) */ '#ff4cd25c',
  OPACITY_PINK_4: /* rgb(100% 30% 82% / 0.04) */ '#ff4cd20a',
  OPACITY_PINK_40: /* rgb(100% 30% 82% / 0.4) */ '#ff4cd266',
  OPACITY_PINK_44: /* rgb(100% 30% 82% / 0.44) */ '#ff4cd270',
  OPACITY_PINK_48: /* rgb(100% 30% 82% / 0.48) */ '#ff4cd27a',
  OPACITY_PINK_52: /* rgb(100% 30% 82% / 0.52) */ '#ff4cd285',
  OPACITY_PINK_56: /* rgb(100% 30% 82% / 0.56) */ '#ff4cd28f',
  OPACITY_PINK_60: /* rgb(100% 30% 82% / 0.6) */ '#ff4cd299',
  OPACITY_PINK_64: /* rgb(100% 30% 82% / 0.64) */ '#ff4cd2a3',
  OPACITY_PINK_68: /* rgb(100% 30% 82% / 0.68) */ '#ff4cd2ad',
  OPACITY_PINK_72: /* rgb(100% 30% 82% / 0.72) */ '#ff4cd2b8',
  OPACITY_PINK_76: /* rgb(100% 30% 82% / 0.76) */ '#ff4cd2c2',
  OPACITY_PINK_8: /* rgb(100% 30% 82% / 0.08) */ '#ff4cd214',
  OPACITY_PINK_80: /* rgb(100% 30% 82% / 0.8) */ '#ff4cd2cc',
  OPACITY_PINK_84: /* rgb(100% 30% 82% / 0.84) */ '#ff4cd2d6',
  OPACITY_PINK_88: /* rgb(100% 30% 82% / 0.88) */ '#ff4cd2e0',
  OPACITY_PINK_92: /* rgb(100% 30% 82% / 0.92) */ '#ff4cd2eb',
  OPACITY_PINK_96: /* rgb(100% 30% 82% / 0.96) */ '#ff4cd2f5',
  OPACITY_RED_1: /* rgb(82% 18% 22% / 0.01) */ '#d22d3903',
  OPACITY_RED_12: /* rgb(82% 18% 22% / 0.12) */ '#d22d391f',
  OPACITY_RED_16: /* rgb(82% 18% 22% / 0.16) */ '#d22d3929',
  OPACITY_RED_20: /* rgb(82% 18% 22% / 0.2) */ '#d22d3933',
  OPACITY_RED_24: /* rgb(82% 18% 22% / 0.24) */ '#d22d393d',
  OPACITY_RED_28: /* rgb(82% 18% 22% / 0.28) */ '#d22d3947',
  OPACITY_RED_32: /* rgb(82% 18% 22% / 0.32) */ '#d22d3952',
  OPACITY_RED_36: /* rgb(82% 18% 22% / 0.36) */ '#d22d395c',
  OPACITY_RED_4: /* rgb(82% 18% 22% / 0.04) */ '#d22d390a',
  OPACITY_RED_40: /* rgb(82% 18% 22% / 0.4) */ '#d22d3966',
  OPACITY_RED_44: /* rgb(82% 18% 22% / 0.44) */ '#d22d3970',
  OPACITY_RED_48: /* rgb(82% 18% 22% / 0.48) */ '#d22d397a',
  OPACITY_RED_52: /* rgb(82% 18% 22% / 0.52) */ '#d22d3985',
  OPACITY_RED_56: /* rgb(82% 18% 22% / 0.56) */ '#d22d398f',
  OPACITY_RED_60: /* rgb(82% 18% 22% / 0.6) */ '#d22d3999',
  OPACITY_RED_64: /* rgb(82% 18% 22% / 0.64) */ '#d22d39a3',
  OPACITY_RED_68: /* rgb(82% 18% 22% / 0.68) */ '#d22d39ad',
  OPACITY_RED_72: /* rgb(82% 18% 22% / 0.72) */ '#d22d39b8',
  OPACITY_RED_76: /* rgb(82% 18% 22% / 0.76) */ '#d22d39c2',
  OPACITY_RED_8: /* rgb(82% 18% 22% / 0.08) */ '#d22d3914',
  OPACITY_RED_80: /* rgb(82% 18% 22% / 0.8) */ '#d22d39cc',
  OPACITY_RED_84: /* rgb(82% 18% 22% / 0.84) */ '#d22d39d6',
  OPACITY_RED_88: /* rgb(82% 18% 22% / 0.88) */ '#d22d39e0',
  OPACITY_RED_92: /* rgb(82% 18% 22% / 0.92) */ '#d22d39eb',
  OPACITY_RED_96: /* rgb(82% 18% 22% / 0.96) */ '#d22d39f5',
  OPACITY_TEAL_1: /* rgb(0% 51% 54% / 0.01) */ '#00818a03',
  OPACITY_TEAL_12: /* rgb(0% 51% 54% / 0.12) */ '#00818a1f',
  OPACITY_TEAL_16: /* rgb(0% 51% 54% / 0.16) */ '#00818a29',
  OPACITY_TEAL_20: /* rgb(0% 51% 54% / 0.2) */ '#00818a33',
  OPACITY_TEAL_24: /* rgb(0% 51% 54% / 0.24) */ '#00818a3d',
  OPACITY_TEAL_28: /* rgb(0% 51% 54% / 0.28) */ '#00818a47',
  OPACITY_TEAL_32: /* rgb(0% 51% 54% / 0.32) */ '#00818a52',
  OPACITY_TEAL_36: /* rgb(0% 51% 54% / 0.36) */ '#00818a5c',
  OPACITY_TEAL_4: /* rgb(0% 51% 54% / 0.04) */ '#00818a0a',
  OPACITY_TEAL_40: /* rgb(0% 51% 54% / 0.4) */ '#00818a66',
  OPACITY_TEAL_44: /* rgb(0% 51% 54% / 0.44) */ '#00818a70',
  OPACITY_TEAL_48: /* rgb(0% 51% 54% / 0.48) */ '#00818a7a',
  OPACITY_TEAL_52: /* rgb(0% 51% 54% / 0.52) */ '#00818a85',
  OPACITY_TEAL_56: /* rgb(0% 51% 54% / 0.56) */ '#00818a8f',
  OPACITY_TEAL_60: /* rgb(0% 51% 54% / 0.6) */ '#00818a99',
  OPACITY_TEAL_64: /* rgb(0% 51% 54% / 0.64) */ '#00818aa3',
  OPACITY_TEAL_68: /* rgb(0% 51% 54% / 0.68) */ '#00818aad',
  OPACITY_TEAL_72: /* rgb(0% 51% 54% / 0.72) */ '#00818ab8',
  OPACITY_TEAL_76: /* rgb(0% 51% 54% / 0.76) */ '#00818ac2',
  OPACITY_TEAL_8: /* rgb(0% 51% 54% / 0.08) */ '#00818a14',
  OPACITY_TEAL_80: /* rgb(0% 51% 54% / 0.8) */ '#00818acc',
  OPACITY_TEAL_84: /* rgb(0% 51% 54% / 0.84) */ '#00818ad6',
  OPACITY_TEAL_88: /* rgb(0% 51% 54% / 0.88) */ '#00818ae0',
  OPACITY_TEAL_92: /* rgb(0% 51% 54% / 0.92) */ '#00818aeb',
  OPACITY_TEAL_96: /* rgb(0% 51% 54% / 0.96) */ '#00818af5',
  OPACITY_WHITE_1: /* rgb(100% 100% 100% / 0.01) */ '#ffffff03',
  OPACITY_WHITE_12: /* rgb(100% 100% 100% / 0.12) */ '#ffffff1f',
  OPACITY_WHITE_16: /* rgb(100% 100% 100% / 0.16) */ '#ffffff29',
  OPACITY_WHITE_20: /* rgb(100% 100% 100% / 0.2) */ '#ffffff33',
  OPACITY_WHITE_24: /* rgb(100% 100% 100% / 0.24) */ '#ffffff3d',
  OPACITY_WHITE_28: /* rgb(100% 100% 100% / 0.28) */ '#ffffff47',
  OPACITY_WHITE_32: /* rgb(100% 100% 100% / 0.32) */ '#ffffff52',
  OPACITY_WHITE_36: /* rgb(100% 100% 100% / 0.36) */ '#ffffff5c',
  OPACITY_WHITE_4: /* rgb(100% 100% 100% / 0.04) */ '#ffffff0a',
  OPACITY_WHITE_40: /* rgb(100% 100% 100% / 0.4) */ '#ffffff66',
  OPACITY_WHITE_44: /* rgb(100% 100% 100% / 0.44) */ '#ffffff70',
  OPACITY_WHITE_48: /* rgb(100% 100% 100% / 0.48) */ '#ffffff7a',
  OPACITY_WHITE_52: /* rgb(100% 100% 100% / 0.52) */ '#ffffff85',
  OPACITY_WHITE_56: /* rgb(100% 100% 100% / 0.56) */ '#ffffff8f',
  OPACITY_WHITE_60: /* rgb(100% 100% 100% / 0.6) */ '#ffffff99',
  OPACITY_WHITE_64: /* rgb(100% 100% 100% / 0.64) */ '#ffffffa3',
  OPACITY_WHITE_68: /* rgb(100% 100% 100% / 0.68) */ '#ffffffad',
  OPACITY_WHITE_72: /* rgb(100% 100% 100% / 0.72) */ '#ffffffb8',
  OPACITY_WHITE_76: /* rgb(100% 100% 100% / 0.76) */ '#ffffffc2',
  OPACITY_WHITE_8: /* rgb(100% 100% 100% / 0.08) */ '#ffffff14',
  OPACITY_WHITE_80: /* rgb(100% 100% 100% / 0.8) */ '#ffffffcc',
  OPACITY_WHITE_84: /* rgb(100% 100% 100% / 0.84) */ '#ffffffd6',
  OPACITY_WHITE_88: /* rgb(100% 100% 100% / 0.88) */ '#ffffffe0',
  OPACITY_WHITE_92: /* rgb(100% 100% 100% / 0.92) */ '#ffffffeb',
  OPACITY_WHITE_96: /* rgb(100% 100% 100% / 0.96) */ '#fffffff5',
  OPACITY_YELLOW_1: /* rgb(63% 39% 0% / 0.01) */ '#a1630003',
  OPACITY_YELLOW_12: /* rgb(63% 39% 0% / 0.12) */ '#a163001f',
  OPACITY_YELLOW_16: /* rgb(63% 39% 0% / 0.16) */ '#a1630029',
  OPACITY_YELLOW_20: /* rgb(63% 39% 0% / 0.2) */ '#a1630033',
  OPACITY_YELLOW_24: /* rgb(63% 39% 0% / 0.24) */ '#a163003d',
  OPACITY_YELLOW_28: /* rgb(63% 39% 0% / 0.28) */ '#a1630047',
  OPACITY_YELLOW_32: /* rgb(63% 39% 0% / 0.32) */ '#a1630052',
  OPACITY_YELLOW_36: /* rgb(63% 39% 0% / 0.36) */ '#a163005c',
  OPACITY_YELLOW_4: /* rgb(63% 39% 0% / 0.04) */ '#a163000a',
  OPACITY_YELLOW_40: /* rgb(63% 39% 0% / 0.4) */ '#a1630066',
  OPACITY_YELLOW_44: /* rgb(63% 39% 0% / 0.44) */ '#a1630070',
  OPACITY_YELLOW_48: /* rgb(63% 39% 0% / 0.48) */ '#a163007a',
  OPACITY_YELLOW_52: /* rgb(63% 39% 0% / 0.52) */ '#a1630085',
  OPACITY_YELLOW_56: /* rgb(63% 39% 0% / 0.56) */ '#a163008f',
  OPACITY_YELLOW_60: /* rgb(63% 39% 0% / 0.6) */ '#a1630099',
  OPACITY_YELLOW_64: /* rgb(63% 39% 0% / 0.64) */ '#a16300a3',
  OPACITY_YELLOW_68: /* rgb(63% 39% 0% / 0.68) */ '#a16300ad',
  OPACITY_YELLOW_72: /* rgb(63% 39% 0% / 0.72) */ '#a16300b8',
  OPACITY_YELLOW_76: /* rgb(63% 39% 0% / 0.76) */ '#a16300c2',
  OPACITY_YELLOW_8: /* rgb(63% 39% 0% / 0.08) */ '#a1630014',
  OPACITY_YELLOW_80: /* rgb(63% 39% 0% / 0.8) */ '#a16300cc',
  OPACITY_YELLOW_84: /* rgb(63% 39% 0% / 0.84) */ '#a16300d6',
  OPACITY_YELLOW_88: /* rgb(63% 39% 0% / 0.88) */ '#a16300e0',
  OPACITY_YELLOW_92: /* rgb(63% 39% 0% / 0.92) */ '#a16300eb',
  OPACITY_YELLOW_96: /* rgb(63% 39% 0% / 0.96) */ '#a16300f5',
  ORANGE_100: /* rgb(100% 97% 96%) */ '#fff8f5',
  ORANGE_130: /* rgb(100% 96% 93%) */ '#fff4ed',
  ORANGE_160: /* rgb(100% 92% 87%) */ '#feeadf',
  ORANGE_200: /* rgb(99% 87% 80%) */ '#fddecd',
  ORANGE_230: /* rgb(99% 81% 70%) */ '#fccfb2',
  ORANGE_260: /* rgb(99% 74% 58%) */ '#fcbd95',
  ORANGE_300: /* rgb(98% 67% 44%) */ '#fbab70',
  ORANGE_330: /* rgb(98% 59% 27%) */ '#fa9746',
  ORANGE_345: /* rgb(95% 53% 16%) */ '#f1882a',
  ORANGE_360: /* rgb(86% 46% 16%) */ '#db7628',
  ORANGE_400: /* rgb(77% 41% 15%) */ '#c46926',
  ORANGE_430: /* rgb(67% 35% 12%) */ '#ac591f',
  ORANGE_460: /* rgb(61% 30% 9.8%) */ '#9b4c19',
  ORANGE_500: /* rgb(55% 25% 7.5%) */ '#8c4013',
  ORANGE_530: /* rgb(48% 22% 6.3%) */ '#7b3710',
  ORANGE_560: /* rgb(43% 19% 5.5%) */ '#6d300e',
  ORANGE_600: /* rgb(37% 17% 4.3%) */ '#5f2b0b',
  ORANGE_630: /* rgb(34% 15% 3.9%) */ '#56260a',
  ORANGE_660: /* rgb(30% 13% 3.5%) */ '#4c2209',
  ORANGE_700: /* rgb(26% 12% 3.5%) */ '#431e09',
  ORANGE_730: /* rgb(23% 10% 2.7%) */ '#3b1a07',
  ORANGE_760: /* rgb(20% 8.6% 2.4%) */ '#331606',
  ORANGE_800: /* rgb(18% 7.5% 2%) */ '#2d1305',
  ORANGE_830: /* rgb(15% 6.3% 2%) */ '#261005',
  ORANGE_860: /* rgb(13% 5.5% 2%) */ '#200e05',
  ORANGE_900: /* rgb(9.8% 5.1% 1.6%) */ '#190d04',
  PARTNER: /* rgb(25% 53% 93%) */ '#4087ed',
  PAYPAL: /* rgb(7.5% 13% 42%) */ '#13216b',
  PINK_1: /* rgb(100% 83% 97%) */ '#ffd3f8',
  PINK_10: /* rgb(100% 75% 94%) */ '#ffbef0',
  PINK_100: /* rgb(0% 0% 0%) */ '#000000',
  PINK_11: /* rgb(100% 74% 94%) */ '#ffbcef',
  PINK_12: /* rgb(100% 73% 94%) */ '#ffbaef',
  PINK_13: /* rgb(100% 72% 93%) */ '#ffb7ee',
  PINK_14: /* rgb(100% 71% 93%) */ '#ffb4ed',
  PINK_15: /* rgb(100% 70% 93%) */ '#ffb2ec',
  PINK_16: /* rgb(100% 69% 93%) */ '#ffafec',
  PINK_17: /* rgb(100% 68% 92%) */ '#ffadeb',
  PINK_18: /* rgb(100% 67% 92%) */ '#ffaaea',
  PINK_19: /* rgb(100% 65% 91%) */ '#ffa7e9',
  PINK_2: /* rgb(100% 82% 97%) */ '#ffd1f7',
  PINK_20: /* rgb(100% 65% 91%) */ '#ffa5e8',
  PINK_21: /* rgb(100% 64% 91%) */ '#ffa2e8',
  PINK_22: /* rgb(100% 62% 91%) */ '#ff9fe7',
  PINK_23: /* rgb(100% 61% 90%) */ '#ff9ce6',
  PINK_24: /* rgb(100% 60% 90%) */ '#ff99e6',
  PINK_25: /* rgb(100% 59% 90%) */ '#ff96e5',
  PINK_26: /* rgb(100% 58% 89%) */ '#ff93e4',
  PINK_27: /* rgb(100% 56% 89%) */ '#ff90e4',
  PINK_28: /* rgb(100% 55% 89%) */ '#ff8de3',
  PINK_29: /* rgb(100% 54% 89%) */ '#ff8ae2',
  PINK_3: /* rgb(100% 81% 97%) */ '#ffcff7',
  PINK_30: /* rgb(100% 53% 89%) */ '#ff87e2',
  PINK_31: /* rgb(100% 52% 88%) */ '#ff84e1',
  PINK_32: /* rgb(100% 50% 88%) */ '#ff80e0',
  PINK_33: /* rgb(100% 49% 88%) */ '#ff7de0',
  PINK_34: /* rgb(100% 48% 87%) */ '#ff7adf',
  PINK_35: /* rgb(100% 46% 87%) */ '#ff76df',
  PINK_36: /* rgb(100% 45% 87%) */ '#ff74de',
  PINK_37: /* rgb(100% 44% 87%) */ '#ff71dd',
  PINK_38: /* rgb(100% 43% 86%) */ '#ff6edc',
  PINK_39: /* rgb(100% 42% 86%) */ '#ff6cdb',
  PINK_4: /* rgb(100% 80% 96%) */ '#ffccf6',
  PINK_40: /* rgb(100% 41% 85%) */ '#ff69da',
  PINK_41: /* rgb(100% 40% 85%) */ '#ff66d9',
  PINK_42: /* rgb(100% 39% 85%) */ '#ff63d8',
  PINK_43: /* rgb(100% 38% 84%) */ '#ff60d7',
  PINK_44: /* rgb(100% 36% 84%) */ '#ff5dd6',
  PINK_45: /* rgb(100% 35% 84%) */ '#ff5ad5',
  PINK_46: /* rgb(100% 34% 83%) */ '#ff57d4',
  PINK_47: /* rgb(100% 33% 83%) */ '#ff53d4',
  PINK_48: /* rgb(100% 31% 83%) */ '#ff50d3',
  PINK_49: /* rgb(100% 30% 82%) */ '#ff4cd2',
  PINK_5: /* rgb(100% 79% 96%) */ '#ffcaf5',
  PINK_50: /* rgb(100% 30% 82%) */ '#ff4cd2',
  PINK_51: /* rgb(98% 29% 80%) */ '#f94bcd',
  PINK_52: /* rgb(95% 29% 78%) */ '#f34ac8',
  PINK_53: /* rgb(93% 29% 77%) */ '#ee49c4',
  PINK_54: /* rgb(91% 28% 75%) */ '#e848bf',
  PINK_55: /* rgb(89% 27% 73%) */ '#e346ba',
  PINK_56: /* rgb(87% 27% 71%) */ '#dd45b6',
  PINK_57: /* rgb(85% 26% 69%) */ '#d843b1',
  PINK_58: /* rgb(82% 26% 68%) */ '#d242ad',
  PINK_59: /* rgb(80% 25% 66%) */ '#cd40a8',
  PINK_6: /* rgb(100% 78% 96%) */ '#ffc8f4',
  PINK_60: /* rgb(78% 25% 64%) */ '#c83fa4',
  PINK_61: /* rgb(76% 24% 63%) */ '#c33da0',
  PINK_62: /* rgb(74% 24% 61%) */ '#bd3c9b',
  PINK_63: /* rgb(72% 23% 59%) */ '#b83a97',
  PINK_64: /* rgb(70% 22% 58%) */ '#b33893',
  PINK_65: /* rgb(68% 22% 56%) */ '#ae378f',
  PINK_66: /* rgb(66% 21% 54%) */ '#a9358a',
  PINK_67: /* rgb(65% 20% 53%) */ '#a53386',
  PINK_68: /* rgb(63% 19% 51%) */ '#a03182',
  PINK_69: /* rgb(61% 19% 49%) */ '#9b307e',
  PINK_7: /* rgb(100% 78% 95%) */ '#ffc6f3',
  PINK_70: /* rgb(59% 18% 48%) */ '#962e7a',
  PINK_71: /* rgb(57% 17% 46%) */ '#912c76',
  PINK_72: /* rgb(55% 16% 45%) */ '#8d2a72',
  PINK_73: /* rgb(53% 16% 43%) */ '#88286e',
  PINK_74: /* rgb(51% 15% 42%) */ '#83276a',
  PINK_75: /* rgb(50% 15% 40%) */ '#7f2566',
  PINK_76: /* rgb(48% 14% 39%) */ '#7a2363',
  PINK_77: /* rgb(46% 13% 37%) */ '#76215f',
  PINK_78: /* rgb(44% 13% 36%) */ '#71205b',
  PINK_79: /* rgb(43% 12% 34%) */ '#6d1e57',
  PINK_8: /* rgb(100% 76% 95%) */ '#ffc3f2',
  PINK_80: /* rgb(41% 11% 33%) */ '#681c53',
  PINK_81: /* rgb(39% 11% 31%) */ '#641b50',
  PINK_82: /* rgb(37% 9.8% 30%) */ '#5f194c',
  PINK_83: /* rgb(36% 9% 28%) */ '#5b1748',
  PINK_84: /* rgb(34% 8.6% 27%) */ '#571645',
  PINK_85: /* rgb(32% 7.8% 25%) */ '#521441',
  PINK_86: /* rgb(31% 7.5% 24%) */ '#4e133e',
  PINK_87: /* rgb(29% 6.7% 23%) */ '#4a113a',
  PINK_88: /* rgb(27% 5.9% 22%) */ '#450f37',
  PINK_89: /* rgb(25% 5.5% 20%) */ '#410e33',
  PINK_9: /* rgb(100% 76% 95%) */ '#ffc1f1',
  PINK_90: /* rgb(24% 5.1% 19%) */ '#3d0d30',
  PINK_91: /* rgb(22% 4.3% 17%) */ '#390b2c',
  PINK_92: /* rgb(21% 3.9% 16%) */ '#350a29',
  PINK_93: /* rgb(19% 3.1% 15%) */ '#310826',
  PINK_94: /* rgb(18% 2.7% 13%) */ '#2d0722',
  PINK_95: /* rgb(16% 2.4% 12%) */ '#28061f',
  PINK_96: /* rgb(14% 2% 11%) */ '#24051b',
  PINK_97: /* rgb(12% 1.6% 8.6%) */ '#1e0416',
  PINK_98: /* rgb(9% 1.2% 6.3%) */ '#170310',
  PINK_99: /* rgb(5.1% 0.78% 3.1%) */ '#0d0208',
  PLAYSTATION: /* rgb(1.2% 18% 53%) */ '#032f87',
  PLUM_23_ALPHA: /* rgb(74% 74% 94% / 0.1) */ '#bcbcf01a',
  PLUM_0: /* rgb(98% 98% 98%) */ '#f9f9fa',
  PLUM_1: /* rgb(95% 95% 96%) */ '#f3f3f4',
  PLUM_10: /* rgb(51% 52% 57%) */ '#818491',
  PLUM_11: /* rgb(42% 44% 49%) */ '#6c6f7c',
  PLUM_12: /* rgb(36% 37% 42%) */ '#5b5e6a',
  PLUM_13: /* rgb(30% 31% 36%) */ '#4d505b',
  PLUM_14: /* rgb(25% 26% 30%) */ '#41434d',
  PLUM_15: /* rgb(22% 23% 26%) */ '#383a43',
  PLUM_16: /* rgb(19% 20% 23%) */ '#31323b',
  PLUM_17: /* rgb(17% 18% 21%) */ '#2c2d35',
  PLUM_18: /* rgb(15% 15% 18%) */ '#26272f',
  PLUM_19: /* rgb(13% 13% 16%) */ '#212229',
  PLUM_2: /* rgb(93% 93% 94%) */ '#ecedef',
  PLUM_20: /* rgb(11% 11% 14%) */ '#1c1d23',
  PLUM_21: /* rgb(9.4% 9.8% 12%) */ '#18191f',
  PLUM_22: /* rgb(8.6% 8.6% 11%) */ '#16161c',
  PLUM_23: /* rgb(7.5% 7.5% 9.4%) */ '#131318',
  PLUM_24: /* rgb(5.9% 6.3% 7.8%) */ '#0f1014',
  PLUM_25: /* rgb(4.7% 4.7% 6.3%) */ '#0c0c10',
  PLUM_26: /* rgb(3.1% 3.1% 4.3%) */ '#08080b',
  PLUM_3: /* rgb(89% 90% 91%) */ '#e4e5e8',
  PLUM_4: /* rgb(87% 87% 88%) */ '#dddee1',
  PLUM_5: /* rgb(83% 84% 85%) */ '#d3d5d9',
  PLUM_6: /* rgb(78% 78% 81%) */ '#c7c8ce',
  PLUM_7: /* rgb(72% 73% 76%) */ '#b8bac1',
  PLUM_8: /* rgb(66% 67% 71%) */ '#a8aab4',
  PLUM_9: /* rgb(58% 59% 64%) */ '#9597a3',
  PREMIUM_NITRO_PINK_DARK: /* rgb(71% 29% 72%) */ '#b64ab7',
  PREMIUM_NITRO_PINK_LIGHT: /* rgb(76% 41% 76%) */ '#c269c3',
  PREMIUM_PERK_BLUE: /* rgb(50% 65% 100%) */ '#80a6ff',
  PREMIUM_PERK_BLUE_ALT: /* rgb(61% 72% 100%) */ '#9cb8ff',
  PREMIUM_PERK_DARK_BLUE: /* rgb(25% 45% 85%) */ '#4173da',
  PREMIUM_PERK_GOLD: /* rgb(98% 65% 10%) */ '#faa61a',
  PREMIUM_PERK_GREEN: /* rgb(53% 86% 77%) */ '#86dcc5',
  PREMIUM_PERK_LIGHT_BLUE: /* rgb(68% 78% 100%) */ '#aec7ff',
  PREMIUM_PERK_ORANGE: /* rgb(99% 59% 29%) */ '#fc964b',
  PREMIUM_PERK_PINK: /* rgb(100% 50% 96%) */ '#ff80f4',
  PREMIUM_PERK_PURPLE: /* rgb(82% 60% 100%) */ '#d09aff',
  PREMIUM_PERK_YELLOW: /* rgb(100% 84% 28%) */ '#fed648',
  PREMIUM_TIER_0_BLUE: /* rgb(0% 49% 76%) */ '#007cc2',
  PREMIUM_TIER_0_BLUE_FOR_GRADIENTS: /* rgb(0% 49% 76%) */ '#007cc2',
  PREMIUM_TIER_0_BLUE_FOR_GRADIENTS_2: /* rgb(9% 46% 81%) */ '#1776cf',
  PREMIUM_TIER_0_HEADER_GRADIENT_1: /* rgb(22% 21% 73%) */ '#3736bb',
  PREMIUM_TIER_0_HEADER_GRADIENT_2: /* rgb(27% 44% 91%) */ '#4670e8',
  PREMIUM_TIER_0_HEADER_GRADIENT_3: /* rgb(51% 47% 92%) */ '#8377eb',
  PREMIUM_TIER_0_HEADER_GRADIENT_4: /* rgb(91% 51% 95%) */ '#e782f1',
  PREMIUM_TIER_0_HEADER_GRADIENT_5: /* rgb(87% 56% 69%) */ '#df90af',
  PREMIUM_TIER_0_PURPLE: /* rgb(35% 40% 95%) */ '#5865f2',
  PREMIUM_TIER_0_PURPLE_FOR_GRADIENTS: /* rgb(35% 40% 95%) */ '#5865f2',
  PREMIUM_TIER_1_BLUE: /* rgb(45% 56% 96%) */ '#738ef5',
  PREMIUM_TIER_1_BLUE_FOR_GRADIENTS: /* rgb(35% 40% 95%) */ '#5865f2',
  PREMIUM_TIER_1_DARK_BLUE_FOR_GRADIENTS: /* rgb(20% 26% 85%) */ '#3442d9',
  PREMIUM_TIER_1_PURPLE: /* rgb(70% 68% 100%) */ '#b3aeff',
  PREMIUM_TIER_2_PINK: /* rgb(89% 57% 67%) */ '#e292aa',
  PREMIUM_TIER_2_PINK_FOR_GRADIENTS: /* rgb(67% 36% 54%) */ '#ab5d8a',
  PREMIUM_TIER_2_PINK_FOR_GRADIENTS_2: /* rgb(72% 24% 76%) */ '#b73ec1',
  PREMIUM_TIER_2_PURPLE: /* rgb(71% 45% 96%) */ '#b473f5',
  PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS: /* rgb(52% 28% 78%) */ '#8547c6',
  PREMIUM_TIER_2_PURPLE_FOR_GRADIENTS_2: /* rgb(72% 27% 76%) */ '#b845c1',
  PRIMARY_100: /* rgb(98% 98% 98%) */ '#f9f9f9',
  PRIMARY_130: /* rgb(95% 95% 96%) */ '#f2f3f5',
  PRIMARY_160: /* rgb(92% 93% 94%) */ '#ebedef',
  PRIMARY_200: /* rgb(89% 90% 91%) */ '#e3e5e8',
  PRIMARY_230: /* rgb(86% 87% 88%) */ '#dbdee1',
  PRIMARY_260: /* rgb(82% 84% 85%) */ '#d2d5d9',
  PRIMARY_300: /* rgb(77% 79% 81%) */ '#c4c9ce',
  PRIMARY_330: /* rgb(71% 73% 76%) */ '#b5bac1',
  PRIMARY_345: /* rgb(65% 67% 70%) */ '#a5abb3',
  PRIMARY_360: /* rgb(58% 61% 64%) */ '#949ba4',
  PRIMARY_400: /* rgb(50% 52% 56%) */ '#80848e',
  PRIMARY_430: /* rgb(43% 44% 47%) */ '#6d6f78',
  PRIMARY_460: /* rgb(36% 37% 40%) */ '#5c5e66',
  PRIMARY_500: /* rgb(31% 31% 35%) */ '#4e5058',
  PRIMARY_530: /* rgb(25% 26% 29%) */ '#41434a',
  PRIMARY_560: /* rgb(22% 23% 25%) */ '#383a40',
  PRIMARY_600: /* rgb(19% 20% 22%) */ '#313338',
  PRIMARY_630: /* rgb(17% 18% 19%) */ '#2b2d31',
  PRIMARY_645: /* rgb(16% 16% 18%) */ '#282a2e',
  PRIMARY_660: /* rgb(14% 14% 16%) */ '#232428',
  PRIMARY_700: /* rgb(12% 12% 13%) */ '#1e1f22',
  PRIMARY_730: /* rgb(10% 11% 12%) */ '#1a1b1e',
  PRIMARY_760: /* rgb(8.6% 9% 9.8%) */ '#161719',
  PRIMARY_800: /* rgb(6.7% 7.1% 7.8%) */ '#111214',
  PRIMARY_830: /* rgb(4.7% 4.7% 5.1%) */ '#0c0c0d',
  PRIMARY_860: /* rgb(2.4% 2.4% 2.7%) */ '#060607',
  PRIMARY_900: /* rgb(0.78% 0.78% 0.78%) */ '#020202',
  RED_100: /* rgb(100% 97% 98%) */ '#fef8f9',
  RED_130: /* rgb(100% 96% 96%) */ '#fef4f4',
  RED_160: /* rgb(100% 92% 92%) */ '#feeaea',
  RED_200: /* rgb(99% 87% 87%) */ '#fdddde',
  RED_230: /* rgb(98% 80% 81%) */ '#fbcdce',
  RED_260: /* rgb(98% 73% 73%) */ '#fbbabb',
  RED_300: /* rgb(98% 65% 66%) */ '#fba6a8',
  RED_330: /* rgb(98% 57% 58%) */ '#fa9193',
  RED_345: /* rgb(98% 47% 48%) */ '#fa777b',
  RED_360: /* rgb(97% 36% 37%) */ '#f85b5f',
  RED_400: /* rgb(95% 25% 26%) */ '#f23f43',
  RED_430: /* rgb(85% 22% 24%) */ '#da373c',
  RED_460: /* rgb(73% 19% 20%) */ '#bb3033',
  RED_500: /* rgb(63% 16% 16%) */ '#a12829',
  RED_530: /* rgb(56% 13% 13%) */ '#8f2022',
  RED_560: /* rgb(50% 11% 12%) */ '#7f1c1e',
  RED_600: /* rgb(44% 9.4% 10%) */ '#70181a',
  RED_630: /* rgb(40% 8.2% 9%) */ '#651517',
  RED_660: /* rgb(35% 7.5% 7.8%) */ '#5a1314',
  RED_700: /* rgb(31% 6.3% 7.1%) */ '#501012',
  RED_730: /* rgb(27% 5.5% 5.9%) */ '#460e0f',
  RED_760: /* rgb(25% 4.3% 4.7%) */ '#3f0b0c',
  RED_800: /* rgb(21% 3.9% 4.3%) */ '#360a0b',
  RED_830: /* rgb(18% 3.5% 3.9%) */ '#2e090a',
  RED_860: /* rgb(16% 3.1% 3.1%) */ '#280808',
  RED_900: /* rgb(13% 2.4% 2.4%) */ '#220606',
  RED_NEW_21: /* rgb(100% 57% 55%) */ '#ff918b',
  RED_NEW_32: /* rgb(96% 42% 41%) */ '#f56c69',
  RED_NEW_37: /* rgb(92% 36% 36%) */ '#eb5b5b',
  RED_NEW_50: /* rgb(82% 18% 22%) */ '#d22d39',
  RED_NEW_54: /* rgb(76% 16% 21%) */ '#c12a35',
  RED_NEW_57: /* rgb(71% 16% 19%) */ '#b42831',
  RED_NEW_61: /* rgb(64% 14% 17%) */ '#a4232c',
  REDDIT: /* rgb(100% 27% 0%) */ '#ff4500',
  RIOT_GAMES: /* rgb(92% 0% 16%) */ '#ea0029',
  ROLE_BLUE: /* rgb(13% 40% 58%) */ '#206694',
  ROLE_BROWN: /* rgb(66% 26% 0%) */ '#a84300',
  ROLE_BURGUNDY: /* rgb(68% 7.8% 34%) */ '#ad1457',
  ROLE_DARK_BLUE: /* rgb(33% 43% 48%) */ '#546e7a',
  ROLE_DARK_GREY: /* rgb(38% 49% 55%) */ '#607d8b',
  ROLE_DARK_PURPLE: /* rgb(44% 21% 54%) */ '#71368a',
  ROLE_DARK_TEAL: /* rgb(6.7% 50% 42%) */ '#11806a',
  ROLE_DEFAULT: /* rgb(60% 67% 71%) */ '#99aab5',
  ROLE_GREEN: /* rgb(12% 55% 30%) */ '#1f8b4c',
  ROLE_GREY: /* rgb(59% 61% 62%) */ '#979c9f',
  ROLE_LIGHT_BLUE: /* rgb(60% 67% 71%) */ '#99aab5',
  ROLE_LIGHT_GREEN: /* rgb(18% 80% 44%) */ '#2ecc71',
  ROLE_LIGHT_GREY: /* rgb(58% 65% 65%) */ '#95a5a6',
  ROLE_MAGENTA: /* rgb(91% 12% 39%) */ '#e91e63',
  ROLE_ORANGE: /* rgb(90% 49% 13%) */ '#e67e22',
  ROLE_PURPLE: /* rgb(61% 35% 71%) */ '#9b59b6',
  ROLE_SALMON: /* rgb(91% 30% 24%) */ '#e74c3c',
  ROLE_SKY_BLUE: /* rgb(20% 60% 86%) */ '#3498db',
  ROLE_TAN: /* rgb(76% 49% 5.5%) */ '#c27c0e',
  ROLE_TEAL: /* rgb(10% 74% 61%) */ '#1abc9c',
  ROLE_TERRACOTTA: /* rgb(60% 18% 13%) */ '#992d22',
  ROLE_YELLOW: /* rgb(95% 77% 5.9%) */ '#f1c40f',
  SAMSUNG: /* rgb(7.8% 16% 63%) */ '#1429a0',
  SKYPE: /* rgb(0% 62% 84%) */ '#009dd7',
  SPOTIFY: /* rgb(11% 73% 33%) */ '#1db954',
  STEAM: /* rgb(9.4% 14% 20%) */ '#182332',
  TEAL_100: /* rgb(96% 98% 99%) */ '#f4fbfd',
  TEAL_130: /* rgb(91% 98% 99%) */ '#e9f9fd',
  TEAL_160: /* rgb(83% 96% 98%) */ '#d3f4fb',
  TEAL_200: /* rgb(69% 94% 98%) */ '#b1eff9',
  TEAL_230: /* rgb(49% 91% 97%) */ '#7ee7f7',
  TEAL_260: /* rgb(37% 86% 94%) */ '#5edbef',
  TEAL_300: /* rgb(28% 80% 89%) */ '#47cbe2',
  TEAL_330: /* rgb(21% 74% 84%) */ '#35bcd5',
  TEAL_345: /* rgb(18% 69% 79%) */ '#2eb0c9',
  TEAL_360: /* rgb(16% 62% 71%) */ '#289fb6',
  TEAL_400: /* rgb(14% 55% 63%) */ '#248da1',
  TEAL_430: /* rgb(13% 48% 55%) */ '#207a8d',
  TEAL_460: /* rgb(11% 42% 49%) */ '#1b6b7c',
  TEAL_500: /* rgb(9% 37% 43%) */ '#175e6d',
  TEAL_530: /* rgb(7.5% 32% 37%) */ '#13525f',
  TEAL_560: /* rgb(5.9% 29% 33%) */ '#0f4954',
  TEAL_600: /* rgb(4.7% 25% 29%) */ '#0c4049',
  TEAL_630: /* rgb(3.9% 22% 26%) */ '#0a3942',
  TEAL_660: /* rgb(3.1% 20% 23%) */ '#08333a',
  TEAL_700: /* rgb(2.4% 18% 20%) */ '#062d34',
  TEAL_730: /* rgb(2% 15% 18%) */ '#05272d',
  TEAL_760: /* rgb(1.6% 13% 15%) */ '#042227',
  TEAL_800: /* rgb(1.2% 11% 13%) */ '#031d21',
  TEAL_830: /* rgb(0.78% 9.8% 11%) */ '#02191d',
  TEAL_860: /* rgb(0.78% 8.6% 9.8%) */ '#021619',
  TEAL_900: /* rgb(0.39% 7.1% 8.2%) */ '#011215',
  TRANSPARENT: /* rgb(0% 0% 0% / 0) */ '#00000000',
  TWITCH: /* rgb(57% 28% 100%) */ '#9147ff',
  TWITCH_SECONDARY: /* rgb(47% 17% 91%) */ '#772ce8',
  TWITTER: /* rgb(11% 63% 95%) */ '#1da1f2',
  WHITE: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_100: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_130: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_160: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_200: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_230: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_260: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_300: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_330: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_345: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_360: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_400: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_430: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_460: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_500: /* rgb(100% 100% 100%) */ '#ffffff',
  WHITE_530: /* rgb(91% 91% 91%) */ '#e8e8e8',
  WHITE_560: /* rgb(81% 81% 81%) */ '#cfcfcf',
  WHITE_600: /* rgb(68% 68% 68%) */ '#adadad',
  WHITE_630: /* rgb(59% 59% 59%) */ '#969696',
  WHITE_660: /* rgb(51% 51% 51%) */ '#838383',
  WHITE_700: /* rgb(40% 40% 40%) */ '#666666',
  WHITE_730: /* rgb(37% 37% 37%) */ '#5f5f5f',
  WHITE_760: /* rgb(35% 35% 35%) */ '#585858',
  WHITE_800: /* rgb(30% 30% 30%) */ '#4d4d4d',
  WHITE_830: /* rgb(23% 23% 23%) */ '#3b3b3b',
  WHITE_860: /* rgb(15% 15% 15%) */ '#262626',
  WHITE_900: /* rgb(5.1% 5.1% 5.1%) */ '#0d0d0d',
  XBOX: /* rgb(6.3% 49% 6.3%) */ '#107c10',
  YELLOW_100: /* rgb(100% 97% 94%) */ '#fff8ef',
  YELLOW_130: /* rgb(100% 96% 91%) */ '#fff4e8',
  YELLOW_160: /* rgb(100% 92% 83%) */ '#ffebd3',
  YELLOW_200: /* rgb(100% 88% 71%) */ '#fee0b6',
  YELLOW_230: /* rgb(99% 82% 55%) */ '#fdd18c',
  YELLOW_260: /* rgb(99% 76% 27%) */ '#fcc145',
  YELLOW_300: /* rgb(94% 70% 20%) */ '#f0b232',
  YELLOW_330: /* rgb(88% 64% 16%) */ '#e1a42a',
  YELLOW_345: /* rgb(83% 60% 14%) */ '#d49824',
  YELLOW_360: /* rgb(75% 53% 11%) */ '#bf861c',
  YELLOW_400: /* rgb(69% 46% 8.2%) */ '#af7615',
  YELLOW_430: /* rgb(60% 40% 5.1%) */ '#9a650d',
  YELLOW_460: /* rgb(54% 34% 3.5%) */ '#8a5709',
  YELLOW_500: /* rgb(49% 29% 1.6%) */ '#7c4b04',
  YELLOW_530: /* rgb(43% 25% 1.6%) */ '#6d4104',
  YELLOW_560: /* rgb(38% 22% 1.2%) */ '#613803',
  YELLOW_600: /* rgb(33% 20% 1.2%) */ '#543203',
  YELLOW_630: /* rgb(30% 18% 1.2%) */ '#4c2d03',
  YELLOW_660: /* rgb(26% 16% 1.2%) */ '#432803',
  YELLOW_700: /* rgb(23% 14% 1.2%) */ '#3b2303',
  YELLOW_730: /* rgb(21% 12% 0.78%) */ '#351e02',
  YELLOW_760: /* rgb(18% 10% 0.78%) */ '#2e1a02',
  YELLOW_800: /* rgb(15% 8.6% 0.78%) */ '#271602',
  YELLOW_830: /* rgb(13% 7.5% 0.78%) */ '#221302',
  YELLOW_860: /* rgb(11% 6.3% 0.78%) */ '#1c1002',
  YELLOW_900: /* rgb(8.6% 5.5% 0.78%) */ '#160e02',
  YELLOW_NEW_22: /* rgb(87% 67% 40%) */ '#deab67',
  YELLOW_NEW_32: /* rgb(78% 56% 27%) */ '#c79045',
  YELLOW_NEW_38: /* rgb(73% 50% 20%) */ '#ba8032',
  YELLOW_NEW_54: /* rgb(58% 35% 1.2%) */ '#955a03',
  YOUTUBE: /* rgb(80% 13% 13%) */ '#cb2120',
};
type RawColors = typeof RawColors;

export const _private = {
  SemanticColors,
  RawColors,
};
